import { themedConfirm } from '../../../lib/themed-dialogs';
// ACM MESH — Infrastruktura
// Uzly (identické karty local/remote), tenant-scoped, enrollment flow
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { DEFAULT_API_TLS_PORT, DEFAULT_MCP_PORT } from '../../../lib/node-ports';
import { IDENTITY_ME_QUERY_KEY, identityEntityId, type IdentityMe } from '../../../lib/identity-me';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Modal,
  Select,
  StatCard,
  StatsGrid,
  StatusDot,
} from '../../../components/ui';
import { toast } from 'sonner';
import { NodeCard } from '../../../components/NodeCard';
import type {} from '../../../components/TenantTree';
import {
  Activity,
  AlertTriangle,
  Archive,
  CheckCircle2,
  ChevronRight,
  Copy,
  Database,
  ExternalLink,
  GitFork,
  Globe,
  Key,
  Link2,
  Loader2,
  Lock,
  Network,
  Plus,
  RefreshCw,
  Search,
  Server,
  Shield,
  Trash2,
  Upload,
  Users,
  Vote,
  Wifi,
  WifiOff,
  XCircle,
} from 'lucide-react';

// Plné CCP komponenty — NodeDetail, NodeServicesPanel, AppHostTab, atd.
// Import z mesh-peers.tsx kde jsou všechny původní CCP komponenty
// Tyto se zobrazí po kliknutí na kartu uzlu
import { MeshPeersPanel, SecurityPanel, SpawnWizard } from '../mesh-peers';
import { TopologyPanel } from '../../../components/mesh/TopologyPanel';
import { TrustPanel } from './TrustPanel';
import type { WidgetRenderProps } from '../../../widgets/types';
import i18n from '../../../lib/i18n';

// ── Types ────────────────────────────────────────────────────────────────────


interface Token {
  id: string;
  token: string;
  label: string;
  uses_left: number | null;
  max_uses: number | null;
  expires_at: string | null;
  created_at: string;
  used_count: number;
}

// ── Widget-first panely (ModuleWorkspace / registry.mesh) ─────────────────────
// Každý export = jedna karta ve WidgetShell. Scope mesh zatím cluster-wide.

/** Role nodu (MCM / peer) — sm karta na fleet panelu. */
export function MeshRoleWidgetPanel(_props: WidgetRenderProps) {
  return <NodeRoleStatus />;
}

/** Topologie cluster / remote / discovered — md/lg karta. */
export function MeshTopologyWidgetPanel(_props: WidgetRenderProps) {
  return <TopologyPanel />;
}

/** Seznam uzlů + spawn/add/scan — lg karta (bez role/topology, ty jsou zvlášť). */
export function InfraFleetPanel(_props: WidgetRenderProps) {
  return <MeshPeersPanel />;
}

export function ResourcesWidgetPanel(_props: WidgetRenderProps) {
  return <ResourcesPanel />;
}

export function HealthWidgetPanel(_props: WidgetRenderProps) {
  return <HealthPanel />;
}

export function SecurityWidgetPanel(_props: WidgetRenderProps) {
  return <SecurityPanel />;
}

export function EnrollmentWidgetPanel(_props: WidgetRenderProps) {
  return <EnrollmentPanel />;
}

export function DiscoveredWidgetPanel(_props: WidgetRenderProps) {
  return <DiscoveredPanel />;
}

export function BootstrapWidgetPanel(_props: WidgetRenderProps) {
  return <BootstrapPanel />;
}

export function TenantsWidgetPanel(_props: WidgetRenderProps) {
  return <TenantsPanel />;
}

export function TrustWidgetPanel(_props: WidgetRenderProps) {
  return <TrustPanel />;
}

export function RoutingWidgetPanel(_props: WidgetRenderProps) {
  return <RoutingAndQuorumPanel />;
}

interface MeshRouteEdge {
  from: string;
  to: string;
  transport: string;
  via: string | null;
  ok: boolean;
  rtt_band: string | null;
  status: string | null;
  measured_at: string;
  expires_at: string | null;
}

interface ClusterVoter {
  stable_id: string;
  label: string;
  local: boolean;
  status: string | null;
}

interface QuorumVoteResult {
  ok: boolean;
  error?: string;
  correlation_id?: string;
  cluster_uid?: string;
  total_voters?: number;
  majority?: number;
  yes?: number;
  no?: number;
  invalid?: number;
  not_responded?: number;
  passed?: boolean;
  tally_passed?: boolean;
  decided?: boolean;
  closed_after_ms?: number;
  audit_persisted?: boolean;
  audit_digest?: string | null;
  audit_error?: string | null;
  voter_ids?: string[];
  votes?: Array<{
    node: string;
    self: boolean;
    ok: boolean;
    vote: boolean | null;
    valid: boolean;
    error?: string;
    signature_verified?: boolean;
    signer_cert_sha256?: string | null;
  }>;
}

interface QuorumAuditEntry {
  correlation_id: string;
  ts: string;
  cluster_uid: string;
  origin_node: string;
  tool: string;
  reason: string;
  tally_passed: boolean;
  decided: boolean;
  yes: number;
  no: number;
  invalid: number;
  not_responded: number;
  closed_after_ms: number;
  signed_votes: number;
  record_digest: string;
}

function routeAge(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

function RoutingAndQuorumPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [toolName, setToolName] = useState('toolbox.self_test');
  const [toolArgs, setToolArgs] = useState('{}');
  const [reason, setReason] = useState(() =>
    t('mesh.quorumDefaultReason', 'Ověření zdraví clusteru'),
  );
  const [timeoutMs, setTimeoutMs] = useState(5000);
  const [lastVote, setLastVote] = useState<QuorumVoteResult | null>(null);

  const routes = useQuery({
    queryKey: ['mesh-routes'],
    queryFn: () => api.mcp<{ ok: boolean; edges: MeshRouteEdge[] }>('mesh.routes', {}),
    refetchInterval: 30_000,
  });
  const voters = useQuery({
    queryKey: ['mesh-cluster-voters'],
    queryFn: () => api.mcp<{
      ok: boolean;
      cluster_uid: string;
      voters: ClusterVoter[];
      total: number;
      majority: number;
    }>('mesh.cluster_voters', {}),
    refetchInterval: 30_000,
  });
  const quorumAudit = useQuery({
    queryKey: ['mesh-quorum-audit'],
    queryFn: () => api.mcp<{ ok: boolean; entries: QuorumAuditEntry[] }>(
      'mesh.quorum_audit',
      { limit: 12 },
    ),
    refetchInterval: 30_000,
  });
  const voteMutation = useMutation({
    mutationFn: (body: { tool: string; args: Record<string, unknown>; reason: string; timeout_ms: number }) =>
      api.mcp<QuorumVoteResult>('mesh.quorum_vote', body),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error || t('mesh.quorumFailed', 'Hlasování selhalo'));
        return;
      }
      setLastVote(result);
      void queryClient.invalidateQueries({ queryKey: ['mesh-quorum-audit'] });
      if (!result.audit_persisted) {
        toast.error(t(
          'mesh.quorumAuditFailed',
          'Hlasování nemá uložený audit, proto zůstalo fail-closed.',
        ));
      } else if (result.passed) toast.success(t('mesh.quorumPassed', 'Quorum schválilo výsledek'));
      else if (result.decided) toast.error(t('mesh.quorumRejected', 'Quorum výsledek zamítlo'));
      else toast.warning(t('mesh.quorumUndecided', 'Quorum zůstalo nerozhodnuté'));
    },
    onError: (error) => toast.error((error as Error).message),
  });

  const runVote = async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(toolArgs || '{}');
    } catch {
      toast.error(t('mesh.quorumArgsInvalid', 'Argumenty nejsou platný JSON'));
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      toast.error(t('mesh.quorumArgsObject', 'Argumenty musí být JSON objekt'));
      return;
    }
    if (!toolName.trim() || reason.trim().length < 3) {
      toast.error(t('mesh.quorumToolReasonRequired', 'Vyplňte tool a důvod hlasování'));
      return;
    }
    const confirmed = await themedConfirm({
      title: t('mesh.quorumConfirmTitle', 'Spustit tool na celém clusteru?'),
      description: t(
        'mesh.quorumConfirmDescription',
        'Tool {{tool}} se provede na každém voliči pod vaší ověřenou identitou. Použijte pouze bezpečný kontrolní tool.',
        { tool: toolName.trim() },
      ),
      confirmLabel: t('mesh.quorumRun', 'Spustit hlasování'),
    });
    if (!confirmed) return;
    voteMutation.mutate({
      tool: toolName.trim(),
      args: parsed as Record<string, unknown>,
      reason: reason.trim(),
      timeout_ms: Math.max(250, Math.min(timeoutMs, 60_000)),
    });
  };

  const edges = routes.data?.edges ?? [];
  const voterRows = voters.data?.voters ?? [];
  const voteByNode = new Map((lastVote?.votes ?? []).map((vote) => [vote.node, vote]));

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <Card>
          <CardHeader>
            <CardTitle icon={GitFork}>{t('mesh.routingTable', 'Routovací tabulka')}</CardTitle>
            <Button
              size='sm'
              variant='ghost'
              onClick={() => void routes.refetch()}
              disabled={routes.isFetching}
            >
              <RefreshCw size={13} className={routes.isFetching ? 'animate-spin' : ''} />
              {t('mesh.refreshRoutes', 'Obnovit')}
            </Button>
          </CardHeader>
          <CardContent className='p-0'>
            {routes.isLoading ? (
              <div className='p-4 text-sm text-gray-500'>{t('mesh.nacitam')}</div>
            ) : edges.length === 0 ? (
              <div className='p-4 text-sm text-gray-500'>
                {t('mesh.noRoutes', 'Zatím nejsou dostupná čerstvá měření tras.')}
              </div>
            ) : (
              <div className='divide-y divide-gray-800/70'>
                {edges.map((edge) => (
                  <div key={`${edge.from}-${edge.to}-${edge.transport}-${edge.via ?? ''}`} className='px-4 py-3'>
                    <div className='flex items-center gap-2 min-w-0'>
                      <StatusDot status={edge.ok ? 'online' : 'offline'} />
                      <code className='text-xs text-gray-300 truncate' title={edge.from}>{edge.from}</code>
                      <span className='text-gray-600'>→</span>
                      <code className='text-xs text-gray-300 truncate' title={edge.to}>{edge.to}</code>
                      <Badge variant={edge.ok ? 'success' : 'error'} className='ml-auto'>
                        {edge.ok ? t('mesh.routeHealthy', 'průchozí') : t('mesh.routeDown', 'nedostupná')}
                      </Badge>
                    </div>
                    <div className='mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500'>
                      <span>{edge.transport}</span>
                      {edge.via && <span>{t('mesh.routeVia', 'přes {{node}}', { node: edge.via })}</span>}
                      {edge.rtt_band && <span>RTT {edge.rtt_band}</span>}
                      {edge.status && <span>{edge.status}</span>}
                      <span>{t('mesh.routeAge', 'stáří {{age}}', { age: routeAge(edge.measured_at) })}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle icon={Vote}>{t('mesh.quorumTitle', 'Clusterové quorum')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            <div className='rounded-lg border border-gray-800 bg-gray-950/40 px-3 py-2 text-xs text-gray-400'>
              {t(
                'mesh.quorumIdentityHint',
                '{{total}} voličů, většina {{majority}}. Volí jen stable_id ze stejného clusteru; spoke a label nejsou autorita.',
                { total: voters.data?.total ?? '—', majority: voters.data?.majority ?? '—' },
              )}
            </div>
            <label className='block'>
              <span className='text-[11px] text-gray-500'>{t('mesh.quorumTool', 'Kontrolní MCP tool')}</span>
              <input
                value={toolName}
                onChange={(event) => setToolName(event.target.value)}
                className='mt-1 w-full rounded-md border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-mono text-gray-200'
                placeholder='toolbox.self_test'
              />
            </label>
            <label className='block'>
              <span className='text-[11px] text-gray-500'>{t('mesh.quorumArgs', 'Argumenty (JSON objekt)')}</span>
              <textarea
                value={toolArgs}
                onChange={(event) => setToolArgs(event.target.value)}
                rows={3}
                className='mt-1 w-full rounded-md border border-gray-700 bg-gray-950 px-3 py-2 text-xs font-mono text-gray-200'
              />
            </label>
            <div className='grid grid-cols-[1fr_120px] gap-3'>
              <label className='block'>
                <span className='text-[11px] text-gray-500'>{t('mesh.quorumReason', 'Důvod / auditní poznámka')}</span>
                <input
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className='mt-1 w-full rounded-md border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-200'
                />
              </label>
              <label className='block'>
                <span className='text-[11px] text-gray-500'>{t('mesh.quorumTimeout', 'Timeout (ms)')}</span>
                <input
                  type='number'
                  min={250}
                  max={60000}
                  value={timeoutMs}
                  onChange={(event) => setTimeoutMs(Number(event.target.value))}
                  className='mt-1 w-full rounded-md border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-200'
                />
              </label>
            </div>
            {voters.isError && (
              <div className='text-xs text-red-400'>
                {t('mesh.quorumVotersUnavailable', 'Autoritativní seznam voličů není dostupný.')}
              </div>
            )}
            <Button
              onClick={() => void runVote()}
              disabled={voteMutation.isPending || voters.isLoading || voters.isError || !voters.data?.ok}
            >
              {voteMutation.isPending ? <Loader2 size={14} className='animate-spin' /> : <Vote size={14} />}
              {t('mesh.quorumRun', 'Spustit hlasování')}
            </Button>
          </CardContent>
        </Card>
      </div>

      {lastVote && (
        <Card>
          <CardHeader>
            <CardTitle icon={Shield}>
              {lastVote.passed
                ? t('mesh.quorumPassed', 'Quorum schválilo výsledek')
                : lastVote.decided
                ? t('mesh.quorumRejected', 'Quorum výsledek zamítlo')
                : t('mesh.quorumUndecided', 'Quorum zůstalo nerozhodnuté')}
            </CardTitle>
            {lastVote.correlation_id && <code className='text-[10px] text-gray-600'>{lastVote.correlation_id}</code>}
          </CardHeader>
          <CardContent>
            <div className='mb-3 flex flex-wrap items-center gap-2 text-xs'>
              <Badge variant={lastVote.audit_persisted ? 'success' : 'error'}>
                {lastVote.audit_persisted
                  ? t('mesh.quorumAuditStored', 'audit uložen')
                  : t('mesh.quorumAuditMissing', 'audit chybí — fail-closed')}
              </Badge>
              {lastVote.audit_digest && (
                <code className='text-[10px] text-gray-600' title={lastVote.audit_digest}>
                  {lastVote.audit_digest.slice(0, 22)}…
                </code>
              )}
              {lastVote.audit_error && <span className='text-red-400'>{lastVote.audit_error}</span>}
            </div>
            <div className='grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3'>
              <StatCard label={t('mesh.quorumYes', 'ANO')} value={lastVote.yes ?? 0} variant='success' />
              <StatCard label={t('mesh.quorumNo', 'NE')} value={lastVote.no ?? 0} variant='error' />
              <StatCard label={t('mesh.quorumInvalid', 'Neplatné')} value={lastVote.invalid ?? 0} variant='warning' />
              <StatCard label={t('mesh.quorumMissing', 'Bez odpovědi')} value={lastVote.not_responded ?? 0} />
              <StatCard label={t('mesh.quorumClosed', 'Uzavřeno')} value={`${lastVote.closed_after_ms ?? 0} ms`} />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2'>
              {(lastVote.voter_ids ?? voterRows.map((voter) => voter.stable_id)).map((nodeId) => {
                const vote = voteByNode.get(nodeId);
                const voter = voterRows.find((item) => item.stable_id === nodeId);
                const label = voter?.label && voter.label !== nodeId ? `${voter.label} · ${nodeId}` : nodeId;
                return (
                  <div key={nodeId} className='rounded-md border border-gray-800 px-3 py-2 flex items-center gap-2'>
                    <StatusDot status={vote?.vote === true ? 'online' : vote ? 'offline' : 'unknown'} />
                    <span className='text-xs text-gray-300 truncate' title={label}>{label}</span>
                    {vote && (
                      <CheckCircle2
                        size={13}
                        className={vote.signature_verified ? 'text-emerald-400' : 'text-red-400'}
                        aria-label={vote.signature_verified
                          ? t('mesh.quorumSignatureVerified', 'Podpis ověřen')
                          : t('mesh.quorumSignatureInvalid', 'Podpis neověřen')}
                      />
                    )}
                    <Badge
                      variant={vote?.vote === true ? 'success' : vote ? 'error' : 'default'}
                      className='ml-auto'
                    >
                      {vote?.vote === true
                        ? t('mesh.quorumYes', 'ANO')
                        : vote?.vote === false
                        ? t('mesh.quorumNo', 'NE')
                        : vote
                        ? t('mesh.quorumInvalid', 'Neplatné')
                        : t('mesh.quorumMissing', 'Bez odpovědi')}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle icon={Archive}>{t('mesh.quorumAuditTitle', 'Append-only audit hlasování')}</CardTitle>
          <Button
            size='sm'
            variant='ghost'
            onClick={() => void quorumAudit.refetch()}
            disabled={quorumAudit.isFetching}
          >
            <RefreshCw size={13} className={quorumAudit.isFetching ? 'animate-spin' : ''} />
            {t('mesh.refreshRoutes', 'Obnovit')}
          </Button>
        </CardHeader>
        <CardContent className='p-0'>
          {quorumAudit.isLoading ? (
            <div className='p-4 text-sm text-gray-500'>{t('mesh.nacitam')}</div>
          ) : quorumAudit.isError ? (
            <div className='p-4 text-sm text-red-400'>
              {t('mesh.quorumAuditUnavailable', 'Audit hlasování není dostupný.')}
            </div>
          ) : (quorumAudit.data?.entries ?? []).length === 0 ? (
            <div className='p-4 text-sm text-gray-500'>
              {t('mesh.quorumAuditEmpty', 'Zatím není uložené žádné podepsané hlasování.')}
            </div>
          ) : (
            <div className='divide-y divide-gray-800/70'>
              {(quorumAudit.data?.entries ?? []).map((entry) => (
                <div key={entry.correlation_id} className='px-4 py-3'>
                  <div className='flex items-center gap-2 min-w-0'>
                    <Badge variant={entry.tally_passed ? 'success' : entry.decided ? 'error' : 'warning'}>
                      {entry.tally_passed
                        ? t('mesh.quorumPassedShort', 'schváleno')
                        : entry.decided
                        ? t('mesh.quorumRejectedShort', 'zamítnuto')
                        : t('mesh.quorumUndecidedShort', 'nerozhodnuto')}
                    </Badge>
                    <code className='text-xs text-gray-300 truncate' title={entry.tool}>{entry.tool}</code>
                    <span className='ml-auto text-[11px] text-gray-500'>{routeAge(entry.ts)}</span>
                  </div>
                  <div className='mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500'>
                    <code title={entry.correlation_id}>{entry.correlation_id}</code>
                    <span>{entry.yes}× ANO · {entry.no}× NE</span>
                    <span>{t('mesh.quorumSignedVotes', '{{count}} podepsaných hlasů', { count: entry.signed_votes })}</span>
                    <code title={entry.record_digest}>{entry.record_digest.slice(0, 18)}…</code>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tree Node Component ─────────────────────────────────────────────────────

interface TreeItem {
  id: string;
  type: string;
  name: string;
  label: string;
  status: string;
  metadata: Record<string, unknown>;
  node_id?: string | null;
  children?: TreeItem[];
}

const TYPE_ICONS: Record<string, { color: string; bg: string }> = {
  organization: { color: 'text-purple-400', bg: 'bg-purple-500/10' },
  tenant: { color: 'text-pink-400', bg: 'bg-pink-500/10' },
  node: { color: 'text-blue-400', bg: 'bg-blue-500/10' },
  section: { color: 'text-gray-400', bg: 'bg-gray-500/10' },
  module: { color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  service: { color: 'text-green-400', bg: 'bg-green-500/10' },
  proxy: { color: 'text-amber-400', bg: 'bg-amber-500/10' },
  certificate: { color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  dns: { color: 'text-sky-400', bg: 'bg-sky-500/10' },
  sso: { color: 'text-violet-400', bg: 'bg-violet-500/10' },
};

const STATUS_DOT: Record<string, string> = {
  active: 'bg-green-500',
  running: 'bg-green-500',
  online: 'bg-green-500',
  stopped: 'bg-gray-600',
  offline: 'bg-gray-600',
  disabled: 'bg-gray-600',
  error: 'bg-red-500',
  degraded: 'bg-orange-500',
  unknown: 'bg-yellow-500',
  applied: 'bg-blue-500',
};

function TreeNode({ node, depth, search, typeFilter, onWatch, onDisableWatch }: {
  node: TreeItem;
  depth: number;
  search?: string;
  typeFilter?: string | null;
  onWatch: (v: { type: string; name: string }) => void;
  onDisableWatch: (type: string, name: string) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(depth < 2);
  const children = node.children ?? [];

  // Filter: check if this node or any descendant matches
  function matchesFilter(n: TreeItem): boolean {
    const matchesType = !typeFilter || n.type === typeFilter;
    const matchesSearch = !search || n.name.toLowerCase().includes(search) ||
      n.label.toLowerCase().includes(search);
    if (matchesType && matchesSearch) return true;
    return (n.children ?? []).some((c) => matchesFilter(c));
  }
  // Container types (node, section, organization, tenant) always show if children match
  const isContainer = ['organization', 'tenant', 'node', 'section'].includes(node.type);
  if ((search || typeFilter) && !isContainer && !matchesFilter(node)) return null;
  const hasChildren = children.length > 0;
  const style = TYPE_ICONS[node.type] ?? TYPE_ICONS.module;
  const dot = STATUS_DOT[node.status] ?? STATUS_DOT.unknown;

  // Section nodes auto-expand and show as header
  if (node.type === 'section') {
    return (
      <div className='mt-2'>
        <button
          onClick={() => setExpanded(!expanded)}
          className='flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-lg hover:bg-gray-800/30'
        >
          <span className='text-[10px] text-gray-600'>{expanded ? '▼' : '▶'}</span>
          <span className='text-xs font-bold text-gray-400 uppercase tracking-wider'>
            {node.label}
          </span>
          <span className='text-[10px] text-gray-600'>({children.length})</span>
        </button>
        {expanded &&
          children.map((c) => (
            <TreeNode
              key={c.id}
              node={c}
              depth={depth + 1}
              search={search}
              typeFilter={typeFilter}
              onWatch={onWatch}
              onDisableWatch={onDisableWatch}
            />
          ))}
      </div>
    );
  }

  // Node — special card styling (identity = stable_id / UUID only)
  if (node.type === 'node') {
    const sid = String(node.metadata?.stable_id || node.name || '');
    const route = node.metadata?.route_name
      ? String(node.metadata.route_name)
      : '';
    const role = node.metadata?.role ? String(node.metadata.role) : '';
    const host = String(node.metadata?.host || node.metadata?.ip || '');
    const online = node.status === 'active' || node.metadata?.online === true;
    const isLocal = node.metadata?.local === true;
    return (
      <Card className={`mt-3 ${online ? 'border-emerald-500/20' : 'border-gray-700/40'}`}>
        <button
          onClick={() => setExpanded(!expanded)}
          className='flex items-center gap-3 w-full text-left p-3'
        >
          <div
            className={`w-2.5 h-2.5 rounded-full ${
              online ? 'bg-emerald-400' : 'bg-gray-600'
            }`}
          />
          <Server size={16} className={online ? 'text-blue-400' : 'text-gray-500'} />
          <div className='flex-1 min-w-0 text-left'>
            <div className='flex items-center gap-2 flex-wrap'>
              <span className='text-sm font-bold text-white'>
                {node.label || route || sid.slice(0, 8)}
              </span>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                  online
                    ? 'text-emerald-400 bg-emerald-500/10'
                    : 'text-gray-500 bg-gray-800'
                }`}
              >
                {online ? 'online' : 'offline'}
              </span>
              {isLocal && (
                <span className='text-[10px] px-1.5 py-0.5 rounded text-blue-400 bg-blue-500/10'>
                  local
                </span>
              )}
              {role && (
                <span className='text-[10px] px-1.5 py-0.5 rounded text-gray-400 bg-gray-800'>
                  {role}
                </span>
              )}
            </div>
            <div className='text-[10px] text-gray-500 font-mono truncate mt-0.5' title={sid}>
              {sid}
              {route && route !== sid ? ` · ${route}` : ''}
              {host ? ` · ${host}` : ''}
            </div>
            {!online && node.metadata?.announced_at && (
              <div className='text-[10px] text-amber-600/80 mt-0.5'>
                {t('mesh.offlineInventory', 'offline — v inventáři zůstává (naposledy {{when}})', {
                  when: String(node.metadata.announced_at).slice(0, 19).replace('T', ' '),
                })}
              </div>
            )}
          </div>
          <span className='text-[10px] text-gray-600'>{children.length} items</span>
          <span className='text-gray-600 text-xs'>{expanded ? '▼' : '▶'}</span>
        </button>
        {expanded && (
          <CardContent className='pt-0 pb-2 px-2'>
            {children.length === 0 && (
              <div className='text-[10px] text-gray-600 px-2 py-1'>
                {t('mesh.noLocalChildResources', 'žádné lokální child resources (remote/spoke)')}
              </div>
            )}
            {children.map((c) => (
              <TreeNode
                key={c.id}
                node={c}
                depth={depth + 1}
                search={search}
                typeFilter={typeFilter}
                onWatch={onWatch}
                onDisableWatch={onDisableWatch}
              />
            ))}
          </CardContent>
        )}
      </Card>
    );
  }

  // Module/service/proxy — indented row
  const indent = Math.max(0, depth - 2) * 16;

  return (
    <div>
      <button
        onClick={() => hasChildren && setExpanded(!expanded)}
        className={`flex items-center gap-2 w-full text-left py-1.5 px-2 rounded-md hover:bg-gray-800/20 ${
          hasChildren ? 'cursor-pointer' : ''
        }`}
        style={{ marginLeft: indent }}
      >
        {hasChildren
          ? <span className='text-[9px] text-gray-600 w-3'>{expanded ? '▼' : '▶'}</span>
          : <span className='w-3' />}
        <div className={`w-1.5 h-1.5 rounded-full ${dot}`} />
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${style.color} ${style.bg}`}>
          {node.type}
        </span>
        <span className='text-xs text-white truncate'>
          {node.label || node.name.split(':').pop()}
        </span>
        {node.type === 'proxy' && (
          <span className='text-[10px] text-blue-400 font-mono'>{node.name}</span>
        )}
        {node.metadata?.version && (
          <span className='text-[9px] text-gray-600'>v{String(node.metadata.version)}</span>
        )}
        {hasChildren && <span className='text-[9px] text-gray-600'>({children.length})</span>}
      </button>
      {expanded && hasChildren &&
        children.map((c) => (
          <TreeNode
            key={c.id}
            node={c}
            depth={depth + 1}
            search={search}
            typeFilter={typeFilter}
            onWatch={onWatch}
            onDisableWatch={onDisableWatch}
          />
        ))}
    </div>
  );
}

// ── Resources Panel — Core Registry ──────────────────────────────────────────

export function ResourcesPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [watchModal, setWatchModal] = useState<{ type: string; name: string } | null>(null);

  // Hierarchický strom z registry.tree tool
  const { data, isLoading } = useQuery({
    queryKey: ['core-resources'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        tree: Array<{
          id: string;
          type: string;
          name: string;
          label: string;
          node_id: string | null;
          status: string;
          metadata: Record<string, unknown>;
          children: unknown[];
        }>;
      }>('registry.tree', {}),
    refetchInterval: 15_000,
  });

  // Flatten tree for search/filter (keep original tree for display)
  function flattenTree(
    nodes: Array<Record<string, unknown>>,
    depth = 0,
  ): Array<Record<string, unknown> & { _depth: number }> {
    const result: Array<Record<string, unknown> & { _depth: number }> = [];
    for (const n of nodes) {
      result.push({ ...n, _depth: depth });
      if (Array.isArray(n.children)) {
        result.push(...flattenTree(n.children as Array<Record<string, unknown>>, depth + 1));
      }
    }
    return result;
  }

  const { data: tplData } = useQuery({
    queryKey: ['health-templates'],
    queryFn: () =>
      api.mcp<{ ok: boolean; templates: Array<{ id: string; label: string; check_type: string }> }>(
        'toolbox.resource_watch',
        { action: 'templates' },
      ),
    staleTime: 60_000,
  });

  // Spokes — jednosměrně napojené remote/zákaznické instance (mimo registry tree).
  // Offline spoke se NESKRÝVÁ — placeholder + poslední známý stav.
  const { data: spokeData } = useQuery({
    queryKey: ['mesh-spokes'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        total: number;
        online: number;
        spokes: Array<{
          node_id: string;
          label: string;
          status: string;
          online: boolean;
          placeholder: boolean;
          edition: string | null;
          tenant_slug: string | null;
          last_seen: string | null;
          ip: string | null;
        }>;
      }>('mesh.spokes', {}),
    refetchInterval: 20_000,
  });
  const spokes = Array.isArray(spokeData?.spokes) ? spokeData.spokes : [];

  const treeRoots = Array.isArray(data?.tree) ? data.tree : [];
  const templates = Array.isArray(tplData?.templates) ? tplData.templates : [];
  const allFlat = flattenTree(treeRoots as Array<Record<string, unknown>>);

  // Hledaný výraz se předává do TreeNode, který si strom filtruje sám
  // rekurzivně (matchesFilter). Počty v záložkách níž jsou ZÁMĚRNĚ z celku,
  // ne z výsledku hledání.
  const q = search.toLowerCase();

  // Counts
  const allGrouped: Record<string, number> = {};
  for (const r of allFlat) allGrouped[String(r.type)] = (allGrouped[String(r.type)] ?? 0) + 1;

  const TYPE_LABEL: Record<string, string> = {
    organization: t('mesh.typeOrganization', 'Organizace'),
    tenant: t('mesh.typeTenant', 'Tenanti'),
    node: t('mesh.typeNode', 'Nody'),
    section: t('mesh.typeSection', 'Sekce'),
    service: t('mesh.typeService', 'Služby'),
    proxy: t('mesh.typeProxy', 'Proxy'),
    certificate: t('mesh.typeCertificate', 'Certifikáty'),
    module: t('mesh.typeModule', 'Moduly'),
    dns: 'DNS',
    sso: 'SSO',
    tool: t('mesh.typeTool', 'Nástroje'),
  };

  const enableWatch = useMutation({
    mutationFn: (
      { type, name, template, mode, on_failure }: {
        type: string;
        name: string;
        template: string;
        mode: string;
        on_failure: string;
      },
    ) =>
      api.mcp('toolbox.resource_watch', {
        action: 'enable',
        type,
        name,
        template,
        mode,
        on_failure,
        config: {},
      }),
    onSuccess: () => {
      toast.success(t('mesh.monitoringZapnut'));
      setWatchModal(null);
      qc.invalidateQueries({ queryKey: ['core-resources'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const disableWatch = useMutation({
    mutationFn: ({ type, name }: { type: string; name: string }) =>
      api.mcp('toolbox.resource_watch', { action: 'disable', type, name }),
    onSuccess: () => {
      toast.success(t('mesh.monitoringVypnut'));
      qc.invalidateQueries({ queryKey: ['core-resources'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='space-y-4'>
      <StatsGrid columns={4}>
        <StatCard
          icon={Server}
          label='Nody'
          value={allFlat.filter((r) => r.type === 'node').length}
          variant='info'
        />
        <StatCard
          icon={Activity}
          label='Online'
          value={allFlat.filter((r) =>
            r.type === 'node' && (r.status === 'active' || r.metadata?.online === true)
          ).length}
          variant='success'
        />
        <StatCard icon={Database} label='Resources' value={allFlat.length} />
        <StatCard icon={Server} label={t('mesh.typu')} value={Object.keys(allGrouped).length} />
      </StatsGrid>
      <p className='text-[11px] text-gray-500'>
        {t('mesh.nodeIdentityHintPrefix', 'Identita nodu =')}{' '}
        <span className='font-mono text-gray-400'>stable_id (UUID)</span>
        {t('mesh.nodeIdentityHintSuffix', '. Label a route_name jsou jen zobrazení. Status = reálný online/offline z announce.')}
      </p>

      {/* Search + type filter */}
      <div className='flex gap-2 flex-wrap items-center'>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('mesh.hledatResource')}
          className='bg-gray-950 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white w-48 focus:outline-none focus:border-blue-500'
        />
        <button
          onClick={() => setTypeFilter(null)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
            !typeFilter
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'text-gray-500 border border-gray-800'
          }`}
        >
          {t('mesh.vse', 'Vše ({{count}})', { count: allFlat.length })}
        </button>
        {Object.entries(allGrouped).map(([type, count]) => (
          <button
            key={type}
            onClick={() => setTypeFilter(typeFilter === type ? null : type)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
              typeFilter === type
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'text-gray-500 border border-gray-800'
            }`}
          >
            {TYPE_LABEL[type] ?? type} ({count})
          </button>
        ))}
      </div>

      {isLoading && (
        <div className='py-8 text-center text-gray-500'>
          <Loader2 size={14} className='animate-spin inline mr-1' />{t('mesh.nacitam')}
        </div>
      )}

      {/* Hierarchický strom z tree API */}
      {treeRoots.map((root) => (
        <TreeNode
          key={root.id}
          node={root}
          depth={0}
          search={q}
          typeFilter={typeFilter}
          onWatch={setWatchModal}
          onDisableWatch={(t, n) => disableWatch.mutate({ type: t, name: n })}
        />
      ))}

      {/* Spokes — jednosměrné remote/zákaznické instance */}
      {spokes.length > 0 && (
        <div className='space-y-2'>
          <div className='flex items-center gap-2 pt-2'>
            <Globe size={13} className='text-purple-400' />
            <span className='text-xs font-semibold text-gray-300'>
              Spokes ({spokeData?.online ?? 0}/{spokes.length} online)
            </span>
            <span className='text-[10px] text-gray-600'>
              {t('mesh.jednosmerneNapojeniOfflineZust')}
            </span>
          </div>
          <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
            {spokes.map((s) => (
              <div
                key={s.node_id}
                className={`rounded-lg border p-3 ${
                  s.online
                    ? 'border-gray-800 bg-gray-950'
                    : 'border-gray-800/60 bg-gray-950/40 opacity-70'
                }`}
              >
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2 min-w-0'>
                    {s.online
                      ? <Wifi size={13} className='text-green-400 shrink-0' />
                      : <WifiOff size={13} className='text-gray-600 shrink-0' />}
                    <span className='text-xs font-medium text-white truncate'>{s.label}</span>
                  </div>
                  <StatusDot status={s.online ? 'running' : 'stopped'} />
                </div>
                <div className='mt-1 text-[10px] text-gray-500 font-mono truncate'>{s.node_id}</div>
                <div className='mt-2 flex flex-wrap gap-1'>
                  {s.edition && <Badge variant='info'>{s.edition}</Badge>}
                  {s.tenant_slug && <Badge>{s.tenant_slug}</Badge>}
                  <Badge variant={s.online ? 'success' : 'default'}>
                    {s.status}
                  </Badge>
                </div>
                {!s.online && (
                  <div className='mt-2 text-[10px] text-gray-600'>
                    {t('mesh.naposledyViden', 'naposledy viděn:')} {s.last_seen ? timeAgoShort(s.last_seen) : t('mesh.nikdy', 'nikdy')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enable watch modal */}
      {watchModal && (
        <WatchConfigModal
          type={watchModal.type}
          name={watchModal.name}
          templates={templates}
          onClose={() => setWatchModal(null)}
          onSave={(cfg) => enableWatch.mutate({ ...watchModal, ...cfg })}
          saving={enableWatch.isPending}
        />
      )}
    </div>
  );
}

/** Krátký „před X" formát pro placeholder spoke (bez závislosti na date lib). */
function timeAgoShort(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function WatchConfigModal({ type, name, templates, onClose, onSave, saving }: {
  type: string;
  name: string;
  templates: Array<{ id: string; label: string; check_type: string }>;
  onClose: () => void;
  onSave: (cfg: { template: string; mode: string; on_failure: string }) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [template, setTemplate] = useState(templates[0]?.id ?? 'http_ping');
  const [mode, setMode] = useState('watchdog');
  const [onFailure, setOnFailure] = useState('alert');


  return (
    <Modal open onClose={onClose} title={`Monitorovat ${type}/${name}`}>
      <div className='space-y-4'>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('mesh.checkSablona')}</label>
          <Select value={template} onChange={(e) => setTemplate(e.target.value)}>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.label} ({t.check_type})</option>
            ))}
          </Select>
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('mesh.rezim')}</label>
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value='watchdog'>{t('mesh.watchdogSledujAReaguj')}</option>
            <option value='ha'>{t('mesh.haAutomatickyFailover')}</option>
          </Select>
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('mesh.akcePriVypadku')}</label>
          <Select value={onFailure} onChange={(e) => setOnFailure(e.target.value)}>
            <option value='alert'>{t('mesh.alertNotifikaceMqttEvent')}</option>
            <option value='restart'>{t('mesh.restartDockerSystemctlRestart')}</option>
            <option value='failover'>{t('mesh.failoverPrepniNaBackup')}</option>
            <option value='none'>{t('mesh.zadnaJenLoguj')}</option>
          </Select>
        </div>
        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={onClose}>{t('mesh.zrusit')}</Button>
          <Button
            variant='primary'
            loading={saving}
            onClick={() => onSave({ template, mode, on_failure: onFailure })}
          >
            {t('mesh.zapnoutMonitoring')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Health Panel — Catalog monitoring ────────────────────────────────────────

type CatalogEntry = {
  key: string;
  source: string;
  updated_at: string;
  value: Record<string, unknown>;
};

type ProbeResult = { ms?: number; status?: number; error?: string; loading?: boolean };

function staleInfo(updated_at: string) {
  const age = Date.now() - new Date(updated_at).getTime();
  if (age < 300_000) {
    return { label: `${Math.round(age / 1000)}s`, cls: 'text-emerald-400 bg-emerald-500/10' };
  }
  if (age < 3_600_000) {
    return { label: `${Math.round(age / 60_000)}m`, cls: 'text-amber-400 bg-amber-500/10' };
  }
  return { label: `${Math.round(age / 3_600_000)}h`, cls: 'text-red-400 bg-red-500/10' };
}

function parseSource(source: string) {
  return source.replace(/^(mqtt:|network:|local:|reconcile:)/, '');
}

/** Dead / legacy node identities that must never appear in Infrastructure Health. */
const DEAD_NODE_SIGNATURES = [
  'acm-p1',
  'maxpraga-acm1',
  '40835520-b1b5-428a-9e07-e8fe221baa60',
  'd563c342c5',
  '8381f543-e6b8-4cad-aa3c-f856f404db49',
];

function isDeadNodeSignature(s: string | undefined | null): boolean {
  if (!s) return false;
  const x = s.toLowerCase();
  return DEAD_NODE_SIGNATURES.some((sig) => x.includes(sig.toLowerCase()));
}

function entryIsZombie(e: CatalogEntry): boolean {
  const blob = `${e.key}\0${e.source}\0${JSON.stringify(e.value ?? {})}`;
  if (isDeadNodeSignature(blob)) return true;
  const nid = e.value?.node_id != null ? String(e.value.node_id) : '';
  if (isDeadNodeSignature(nid)) return true;
  if (isDeadNodeSignature(parseSource(e.source || ''))) return true;
  // garbage test keys
  if (e.key.startsWith('_test:') || e.key === 'test-unjoin-key') return true;
  return false;
}

/** Prefer value.node_id / stable identity over reconcile:hostname source. */
function entryNodeKey(e: CatalogEntry): string {
  const v = e.value ?? {};
  const fromVal = String(
    v.stable_id || v.node_id || v.hostname || '',
  ).trim();
  if (fromVal && !isDeadNodeSignature(fromVal)) return fromVal;
  const fromSrc = parseSource(e.source || '');
  if (fromSrc && !isDeadNodeSignature(fromSrc)) return fromSrc;
  return fromSrc || 'unknown';
}

export function HealthPanel() {
  const { t } = useTranslation();
  const [probes, setProbes] = useState<Record<string, ProbeResult>>({});
  const [filter, setFilter] = useState<'services' | 'network' | null>(null);

  const { data: svcData, isLoading: svcLoading, refetch: refetchSvc } = useQuery({
    queryKey: ['catalog-services'],
    queryFn: () =>
      api.mcp<{ ok: boolean; entries: CatalogEntry[] }>(
        'catalog.list',
        { registry_id: 'services', limit: 200 },
      ),
    refetchInterval: 30_000,
  });

  const { data: netData, isLoading: netLoading, refetch: refetchNet } = useQuery({
    queryKey: ['catalog-network'],
    queryFn: () =>
      api.mcp<{ ok: boolean; entries: CatalogEntry[] }>(
        'catalog.list',
        { registry_id: 'network', limit: 200 },
      ),
    refetchInterval: 30_000,
  });

  async function probe(key: string, url: string) {
    setProbes((p) => ({ ...p, [key]: { loading: true } }));
    try {
      const r = await api.mcp<
        { ok: boolean; latency_ms?: number; status?: number; error?: string }
      >(
        'debug.http_probe',
        { url: url.startsWith('http') ? url : `https://${url}`, timeout_ms: 6000 },
      );
      setProbes((p) => ({ ...p, [key]: { ms: r.latency_ms, status: r.status, error: r.error } }));
    } catch (e) {
      setProbes((p) => ({ ...p, [key]: { error: (e as Error).message } }));
    }
  }

  // ACM instances from services registry — drop zombies (acm-p1, dead hashes)
  const svcEntries = (Array.isArray(svcData?.entries) ? svcData.entries : [])
    .filter((e) => !entryIsZombie(e));
  const netEntries = (Array.isArray(netData?.entries) ? netData.entries : [])
    .filter((e) => !entryIsZombie(e));
  const acmInstances = svcEntries.filter((e) => e.value?.module === 'acm-self');

  // Group ACM instances by node identity (UUID preferred)
  const byNode: Record<string, CatalogEntry[]> = {};
  for (const e of acmInstances) {
    const node = entryNodeKey(e);
    if (isDeadNodeSignature(node)) continue;
    (byNode[node] ??= []).push(e);
  }

  // Other services entries (non-acm-self)
  const otherSvc = svcEntries.filter((e) =>
    e.value?.module !== 'acm-self' && e.value?.module
  );

  // Network URL entries (real domains only)
  const netUrls = netEntries.filter((e) =>
    e.key.startsWith('url/') &&
    typeof e.value?.url === 'string' &&
    (e.value.url as string).includes('.') &&
    !(e.value.url as string).startsWith('test')
  );

  // Group network URLs by node_id
  const netByNode: Record<string, CatalogEntry[]> = {};
  for (const e of netUrls) {
    const node = entryNodeKey(e);
    if (isDeadNodeSignature(node)) continue;
    (netByNode[node] ??= []).push(e);
  }

  const allEntries = [...acmInstances, ...otherSvc, ...netUrls];
  const freshCount =
    allEntries.filter((e) => Date.now() - new Date(e.updated_at).getTime() < 300_000).length;
  const staleCount = allEntries.filter((e) => {
    const age = Date.now() - new Date(e.updated_at).getTime();
    return age >= 300_000 && age < 3_600_000;
  }).length;
  const oldCount =
    allEntries.filter((e) => Date.now() - new Date(e.updated_at).getTime() >= 3_600_000).length;

  const isLoading = svcLoading || netLoading;

  return (
    <div className='space-y-4'>
      {/* Summary stats */}
      <StatsGrid columns={4}>
        <StatCard icon={Activity} label={t('mesh.celkemPolozek')} value={allEntries.length} />
        <StatCard icon={CheckCircle2} label={t('mesh.cerstve5m')} value={freshCount} variant='success' />
        <StatCard
          icon={AlertTriangle}
          label={t('mesh.starsi560m')}
          value={staleCount}
          variant='warning'
        />
        <StatCard icon={XCircle} label={t('mesh.stare1h')} value={oldCount} variant='error' />
      </StatsGrid>

      {/* Filter tabs */}
      <div className='flex items-center gap-2'>
        {(['services', 'network'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(filter === f ? null : f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === f
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'text-gray-500 border border-gray-800 hover:border-gray-600'
            }`}
          >
            {f === 'services'
              ? t('mesh.acmServicesCount', 'ACM služby ({{count}})', { count: acmInstances.length })
              : t('mesh.urlNetCount', 'URL síť ({{count}})', { count: netUrls.length })}
          </button>
        ))}
        <button
          onClick={() => {
            refetchSvc();
            refetchNet();
          }}
          className='ml-auto text-gray-600 hover:text-gray-300 p-1.5 rounded-lg border border-gray-800 hover:border-gray-600 transition-colors'
          title='Obnovit'
        >
          <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} />
        </button>
      </div>

      {isLoading && (
        <div className='py-6 text-center text-gray-500 text-sm'>
          <Loader2 size={14} className='animate-spin inline mr-1' /> {t('mesh.nacitamKatalog')}
        </div>
      )}

      {/* ACM Services — grouped by source node */}
      {(filter === null || filter === 'services') && (
        <div className='space-y-3'>
          <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2'>
            <Server size={12} /> {t('mesh.acmInstanceVKatalogu')}
          </p>
          {Object.entries(byNode).map(([node, entries]) => (
            <Card key={node}>
              <CardHeader>
                <CardTitle icon={Server}>
                  {node}
                  <span className='text-gray-500 font-normal ml-1'>({entries.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className='p-0 divide-y divide-gray-800/30'>
                {entries.map((e) => {
                  const domains = e.value?.domains as Record<string, string> | undefined;
                  const hash = (e.value?.hash as string | undefined)?.slice(0, 8) ??
                    e.key.split(':')[1]?.slice(0, 8);
                  const dashUrl = domains?.dashboard;
                  const st = staleInfo(e.updated_at);
                  const pk = `svc:${e.key}`;
                  const pr = probes[pk];
                  return (
                    <div key={e.key} className='flex items-center gap-3 px-4 py-2.5'>
                      <StatusDot
                        status={st.cls.includes('emerald')
                          ? 'running'
                          : st.cls.includes('amber')
                          ? 'pending'
                          : 'stopped'}
                      />
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <span className='text-xs font-mono text-white'>{hash}</span>
                          {dashUrl && (
                            <a
                              href={`https://${dashUrl}`}
                              target='_blank'
                              rel='noreferrer'
                              className='text-[10px] text-blue-400 hover:underline flex items-center gap-0.5'
                            >
                              {dashUrl.split('.')[0]} <ExternalLink size={9} />
                            </a>
                          )}
                          {(['api', 'mcp', 'mqtts', 'agents'] as const).map((ep) => {
                            const enabled = e.value?.[`${ep}_public_enabled`] as
                              | boolean
                              | undefined;
                            if (!enabled) return null;
                            return (
                              <span
                                key={ep}
                                className='text-[9px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-400'
                              >
                                {ep}
                              </span>
                            );
                          })}
                        </div>
                        <div className='text-[10px] text-gray-600 mt-0.5'>
                          katalog: {new Date(e.updated_at).toLocaleString('cs')}
                          {pr && !pr.loading && (
                            <span
                              className={`ml-2 ${
                                pr.error
                                  ? 'text-red-400'
                                  : pr.status && pr.status < 400
                                  ? 'text-emerald-400'
                                  : 'text-amber-400'
                              }`}
                            >
                              {pr.error
                                ? `chyba: ${pr.error.slice(0, 40)}`
                                : `${pr.ms}ms · HTTP ${pr.status}`}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`text-[9px] px-1.5 py-0.5 rounded font-medium shrink-0 ${st.cls}`}
                      >
                        {st.label}
                      </span>
                      {dashUrl && (
                        <button
                          onClick={() => probe(pk, dashUrl)}
                          disabled={pr?.loading}
                          className='text-gray-600 hover:text-blue-400 p-1 shrink-0 disabled:opacity-40'
                          title={t('mesh.prozkoumatHttpOdezvu')}
                        >
                          {pr?.loading
                            ? <Loader2 size={12} className='animate-spin' />
                            : <Wifi size={12} />}
                        </button>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}

          {/* Other services (non-acm) */}
          {otherSvc.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle icon={Globe}>{t('mesh.ostatniSluzby')}</CardTitle>
              </CardHeader>
              <CardContent className='p-0 divide-y divide-gray-800/30'>
                {otherSvc.map((e) => {
                  const st = staleInfo(e.updated_at);
                  return (
                    <div key={e.key} className='flex items-center gap-3 px-4 py-2.5'>
                      <StatusDot status={st.cls.includes('emerald') ? 'running' : 'stopped'} />
                      <span className='text-xs text-white flex-1'>{e.key}</span>
                      <span className='text-[10px] text-gray-600'>{parseSource(e.source)}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${st.cls}`}>
                        {st.label}
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Network URLs — grouped by node */}
      {(filter === null || filter === 'network') && netUrls.length > 0 && (
        <div className='space-y-3'>
          <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2'>
            <Network size={12} /> {t('mesh.urlSitNetworkRegistry')}
          </p>
          {Object.entries(netByNode).map(([node, entries]) => (
            <Card key={node}>
              <CardHeader>
                <CardTitle icon={Globe}>
                  {node}
                  <span className='text-gray-500 font-normal ml-1'>({entries.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className='p-0 divide-y divide-gray-800/30'>
                {entries.map((e) => {
                  const url = e.value?.url as string;
                  const service = e.value?.service as string | undefined;
                  const st = staleInfo(e.updated_at);
                  const pk = `net:${e.key}`;
                  const pr = probes[pk];
                  return (
                    <div key={e.key} className='flex items-center gap-3 px-4 py-2.5'>
                      <StatusDot
                        status={st.cls.includes('emerald')
                          ? 'running'
                          : st.cls.includes('amber')
                          ? 'pending'
                          : 'stopped'}
                      />
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <span className='text-xs font-mono text-white truncate'>{url}</span>
                          {service && (
                            <span className='text-[9px] px-1 py-0.5 rounded bg-gray-800 text-gray-500 shrink-0'>
                              {service}
                            </span>
                          )}
                        </div>
                        {pr && !pr.loading && (
                          <div
                            className={`text-[10px] mt-0.5 ${
                              pr.error
                                ? 'text-red-400'
                                : pr.status && pr.status < 400
                                ? 'text-emerald-400'
                                : 'text-amber-400'
                            }`}
                          >
                            {pr.error ? pr.error.slice(0, 60) : `${pr.ms}ms · HTTP ${pr.status}`}
                          </div>
                        )}
                      </div>
                      <span
                        className={`text-[9px] px-1.5 py-0.5 rounded font-medium shrink-0 ${st.cls}`}
                      >
                        {st.label}
                      </span>
                      <button
                        onClick={() => probe(pk, url)}
                        disabled={pr?.loading}
                        className='text-gray-600 hover:text-blue-400 p-1 shrink-0 disabled:opacity-40'
                        title={t('mesh.prozkoumatHttpOdezvu')}
                      >
                        {pr?.loading
                          ? <Loader2 size={12} className='animate-spin' />
                          : <Wifi size={12} />}
                      </button>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && allEntries.length === 0 && (
        <EmptyState
          icon={<Network size={24} />}
          title={t('mesh.katalogJePrazdny')}
          description={t('mesh.zadneZaznamyVServices')}
        />
      )}
    </div>
  );
}

// ── Enrollment Panel (kompletní) ─────────────────────────────────────────────

export function EnrollmentPanel() {
  return (
    <div className='space-y-6'>
      <RemoteAddCard />
      <div className='grid gap-4 md:grid-cols-2'>
        {/* Ruční přidání cluster peer */}
        <ManualAddCard />

        {/* Scan sítě */}
        <ScanCard />

        {/* Enrollment token */}
        <TokenCard />

        {/* Spawn (Proxmox) */}
        <SpawnCard />
      </div>

      {/* Existující tokeny */}
      <TokensList />
    </div>
  );
}

type RemoteAddResponse = {
  ok?: boolean;
  error?: string;
  stable_id?: string;
  discovery_id?: string;
  cert_fingerprint?: string;
  confirmation_required?: boolean;
};

function RemoteAddCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [host, setHost] = useState('');
  const [label, setLabel] = useState('');
  const [tenant, setTenant] = useState('');
  const [bindEmail, setBindEmail] = useState('');
  const [fingerprintAccepted, setFingerprintAccepted] = useState(false);
  const [pending, setPending] = useState<{
    discoveryId: string;
    stableId?: string;
    fingerprint: string;
  } | null>(null);

  const { data: me } = useQuery({
    queryKey: IDENTITY_ME_QUERY_KEY,
    queryFn: () => api.mcp<IdentityMe>('identity.me'),
    staleTime: 60_000,
  });
  const myId = identityEntityId(me);
  const tenantsQ = useQuery({
    queryKey: ['mesh-tenants'],
    queryFn: () =>
      api.mcp<{ ok: boolean; tenants: Array<{ slug: string; name: string }> }>('mesh.tenants', {
        action: 'list',
      }),
  });
  const tenants = tenantsQ.data?.tenants ?? [];

  const resetDiscovery = () => {
    setPending(null);
    setFingerprintAccepted(false);
  };
  const addMut = useMutation({
    mutationFn: () =>
      api.mcp<RemoteAddResponse>('mesh.remote_add', {
        ...(pending ? { discovery_id: pending.discoveryId } : { host: host.trim() }),
        ...(pending && fingerprintAccepted ? { confirmed_fingerprint: pending.fingerprint } : {}),
        tenant_slug: tenant,
        label: label.trim() || undefined,
        enroll: true,
        bind_users: myId
          ? [
              {
                entity_id: myId,
                bind_email: bindEmail.trim() || undefined,
                modules: [
                  { module: 'mesh', role: 'admin' },
                  { module: 'acm-self', role: 'operator' },
                  { module: 'identity', role: 'operator' },
                ],
              },
            ]
          : undefined,
      }),
    onSuccess: (response) => {
      if (response.confirmation_required && response.discovery_id && response.cert_fingerprint) {
        setPending({
          discoveryId: response.discovery_id,
          stableId: response.stable_id,
          fingerprint: response.cert_fingerprint,
        });
        setFingerprintAccepted(false);
        toast.info(t('mesh.confirmRemoteFingerprint', 'Zkontroluj a potvrď fingerprint'));
        return;
      }
      if (!response.ok) {
        toast.error(response.error ?? t('mesh.remoteAddFailed', 'Přidání remote selhalo'));
        return;
      }
      toast.success(
        t('mesh.remoteAdded', 'Remote {{id}} připojen', {
          id: response.stable_id ?? host,
        }),
      );
      for (const key of [
        ['mesh-topology'],
        ['mesh-spokes'],
        ['mesh-trust-overview'],
        ['mesh-discovered'],
      ])
        void qc.invalidateQueries({ queryKey: key });
      setHost('');
      setLabel('');
      setBindEmail('');
      resetDiscovery();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Link2}>{t('mesh.addRemoteAcm', 'Přidat remote ACM')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-500">
          {t(
            'mesh.addRemoteAcmHint',
            'Existující ACM s vlastní Root CA: discovery, potvrzení certifikátu, enroll, user grant a MQTT bridge.',
          )}
        </p>
        <input
          value={host}
          onChange={(event) => {
            setHost(event.target.value);
            resetDiscovery();
          }}
          disabled={!!pending}
          placeholder={`10.3.109.122 nebo https://a01.apollo-it.cz:${DEFAULT_MCP_PORT}`}
          className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white disabled:opacity-60"
        />
        <div className="flex gap-2">
          <Select
            value={tenant}
            onChange={(event) => {
              setTenant(event.target.value);
              resetDiscovery();
            }}
            className="flex-1 px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white"
          >
            <option value="">{t('mesh.pickTenant', 'Tenant…')}</option>
            {tenants.map((item) => (
              <option key={item.slug} value={item.slug}>
                {item.name} ({item.slug})
              </option>
            ))}
          </Select>
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder={t('mesh.popisekVolitelny')}
            className="flex-1 px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white"
          />
        </div>
        <input
          value={bindEmail}
          onChange={(event) => setBindEmail(event.target.value)}
          placeholder={t('mesh.targetAccountEmail', 'E-mail existujícího účtu na cíli (volitelné)')}
          className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white"
        />
        {pending && (
          <div className="rounded border border-amber-700/60 bg-amber-950/20 p-3 space-y-2">
            <div className="text-xs text-amber-200">
              {t('mesh.remoteFingerprintFor', 'TLS fingerprint pro {{id}}', {
                id: pending.stableId ?? host,
              })}
            </div>
            <code className="block break-all text-[11px] text-zinc-200 bg-zinc-950 p-2 rounded">
              {pending.fingerprint}
            </code>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input
                type="checkbox"
                checked={fingerprintAccepted}
                onChange={(event) => setFingerprintAccepted(event.target.checked)}
              />
              {t('mesh.fingerprintChecked', 'Fingerprint jsem ověřil a potvrzuji tento remote')}
            </label>
            <Button variant="secondary" size="sm" onClick={resetDiscovery}>
              {t('mesh.cancelDiscovery', 'Zrušit a objevit znovu')}
            </Button>
          </div>
        )}
        <Button
          variant="primary"
          size="sm"
          className="w-full"
          disabled={
            !host.trim() || !tenant || (!!pending && !fingerprintAccepted) || addMut.isPending
          }
          onClick={() => addMut.mutate()}
        >
          {addMut.isPending && <Loader2 size={14} className="inline animate-spin mr-1" />}
          {pending
            ? t('mesh.confirmAndConnectRemote', 'Potvrdit a připojit remote')
            : t('mesh.discoverRemote', 'Objevit remote')}
        </Button>
      </CardContent>
    </Card>
  );
}

function ManualAddCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(String(DEFAULT_API_TLS_PORT));
  const [label, setLabel] = useState('');

  const addMut = useMutation({
    mutationFn: () =>
      api.mcp('mesh.peers_add', { name, host, api_port: parseInt(port), label: label || name }),
    onSuccess: () => {
      toast.success(t('mesh.nodeAdded', 'Uzel {{name}} přidán', { name }));
      qc.invalidateQueries({ queryKey: ['ccp-nodes'] });
      setName('');
      setHost('');
      setLabel('');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Server}>{t('mesh.rucniPridani')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-2'>
        <p className='text-xs text-gray-500'>{t('mesh.znamIpAdresuExistujiciho')}</p>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('mesh.nazevSlug')}
          className='w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
        />
        <div className='flex gap-2'>
          <input
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder={t('mesh.ipAdresa')}
            className='flex-1 px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
          />
          <input
            value={port}
            onChange={(e) => setPort(e.target.value)}
            placeholder='Port'
            className='w-20 px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
          />
        </div>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('mesh.popisekVolitelny')}
          className='w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
        />
        <Button
          variant='primary'
          size='sm'
          onClick={() => addMut.mutate()}
          disabled={!name || !host || addMut.isPending}
          className='w-full'
        >
          {addMut.isPending && <Loader2 size={14} className='animate-spin' />}
          {t('mesh.addNode', 'Přidat uzel')}
        </Button>
      </CardContent>
    </Card>
  );
}

function ScanCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [cidr, setCidr] = useState('');
  const [result, setResult] = useState<{
    found: number;
    scanned: number;
    open: number;
    nodes: Array<{ host: string; acm: boolean; stage?: string }>;
  } | null>(null);

  const scanMut = useMutation({
    mutationFn: async () => {
      const raw = cidr.trim();
      if (!raw) throw new Error('Zadej IP nebo CIDR (max /24)');
      // Jednorázový rozsah → mesh.scan_range; jedna IP taky (jako /32)
      const r = await api.mcp<{
        ok: boolean;
        error?: string;
        hosts_scanned?: number;
        open_hosts?: string[];
        discovered?: Array<{
          address?: string;
          host?: string;
          stage?: string;
          stable_id?: string;
        }>;
        rejected?: Array<{ address?: string }>;
      }>('mesh.scan_range', { cidr: raw.includes('/') ? raw : raw });
      if (r.ok === false && r.error) throw new Error(r.error);
      const acmHosts = new Map<string, string | undefined>();
      for (const d of r.discovered ?? []) {
        const h = d.host ?? d.address;
        if (h) acmHosts.set(h, d.stage);
      }
      const nodes: Array<{ host: string; acm: boolean; stage?: string }> = [];
      for (const h of r.open_hosts ?? []) {
        nodes.push({
          host: h,
          acm: acmHosts.has(h),
          stage: acmHosts.get(h),
        });
      }
      for (const [h, stage] of acmHosts) {
        if (!nodes.some((n) => n.host === h)) {
          nodes.push({ host: h, acm: true, stage });
        }
      }
      return {
        found: nodes.filter((n) => n.acm).length,
        scanned: r.hosts_scanned ?? 0,
        open: (r.open_hosts ?? []).length,
        nodes,
      };
    },
    onSuccess: (r) => {
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['mesh-discovered'] });
      void qc.invalidateQueries({ queryKey: ['mesh-discovered-count'] });
      if (r.found > 0) {
        toast.success(t('mesh.scanFound', 'Nalezeno {{found}} ACM (open {{open}}/{{scanned}})', {
          found: r.found,
          open: r.open,
          scanned: r.scanned,
        }));
      } else {
        toast.message(t('mesh.scanNone', 'Sken hotov: open {{open}}/{{scanned}}, žádný ACM beacon', {
          open: r.open,
          scanned: r.scanned,
        }));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Search}>{t('mesh.scanSite')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-2'>
        <p className='text-xs text-gray-500'>
          {t(
            'mesh.scanHint',
            'Jednorázový sken rozsahu (max /24): TCP 9082/9080 → public beacon → discovery fronta. Internetový maják to není.',
          )}
        </p>
        <div className='flex gap-2'>
          <input
            value={cidr}
            onChange={(e) => setCidr(e.target.value)}
            placeholder='10.119.251.0/24'
            className='flex-1 px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
          />
          <Button
            variant='primary'
            size='sm'
            onClick={() => scanMut.mutate()}
            disabled={!cidr || scanMut.isPending}
          >
            {scanMut.isPending
              ? <Loader2 size={14} className='animate-spin' />
              : <Search size={14} />}
            Scan
          </Button>
        </div>
        {result && (
          <div className='space-y-1 mt-2'>
            <p className='text-[10px] text-gray-400'>
              ACM: {result.found} · open: {result.open}/{result.scanned}
            </p>
            {result.nodes.filter((n) => n.acm).map((n) => (
              <div
                key={n.host}
                className='flex items-center justify-between p-1.5 bg-gray-800/50 rounded text-xs'
              >
                <span className='font-mono text-white'>{n.host}</span>
                <Badge variant='green'>{n.stage ?? 'ACM'}</Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TokenCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [maxUses, setMaxUses] = useState('1');
  const [expiry, setExpiry] = useState('24');

  const createMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; token: string }>('mesh.tokens', {
        action: 'create',
        label: label || `node-${Date.now()}`,
        max_uses: parseInt(maxUses) || null,
        expires_hours: parseInt(expiry) || 24,
      }),
    onSuccess: (r) => {
      if (r.token) {
        navigator.clipboard.writeText(r.token);
        toast.success(t('mesh.tokenVytvorenAZkopirovan'));
      }
      qc.invalidateQueries({ queryKey: ['mesh-tokens'] });
      setLabel('');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Key}>{t('mesh.enrollmentToken')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-2'>
        <p className='text-xs text-gray-500'>
          {t('mesh.novyServerSeSam')}
        </p>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('mesh.popisekNaprProdNode')}
          className='w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
        />
        <div className='flex gap-2'>
          <div className='flex-1'>
            <label className='text-[10px] text-gray-600'>{t('mesh.maxPouziti')}</label>
            <input
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder='1'
              className='w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
            />
          </div>
          <div className='flex-1'>
            <label className='text-[10px] text-gray-600'>{t('mesh.platnostHodiny')}</label>
            <input
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              placeholder='24'
              className='w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
            />
          </div>
        </div>
        <Button
          variant='primary'
          size='sm'
          onClick={() => createMut.mutate()}
          disabled={createMut.isPending}
          className='w-full'
        >
          {createMut.isPending && <Loader2 size={14} className='animate-spin' />}
          {t('mesh.createTokenCopy', 'Vytvořit token & kopírovat')}
        </Button>
      </CardContent>
    </Card>
  );
}

function SpawnCard() {
  const { t } = useTranslation();
  const [showWizard, setShowWizard] = useState(false);
  const qc = useQueryClient();
  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Upload}>{t('mesh.spawnNovyServer')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-2'>
        <p className='text-xs text-gray-500'>
          {t('mesh.vytvoriNovyVmV')}
        </p>
        <Button variant='primary' size='sm' className='w-full' onClick={() => setShowWizard(true)}>
          {t('mesh.spustitSpawnWizard')}
        </Button>
        {showWizard && (
          <SpawnWizard
            onClose={() => setShowWizard(false)}
            onSpawned={() => {
              setShowWizard(false);
              qc.invalidateQueries({ queryKey: ['ccp-nodes'] });
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function TokensList() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['mesh-tokens'],
    queryFn: () => api.mcp<{ ok: boolean; tokens: Token[] }>('mesh.tokens', { action: 'list' }),
  });
  const tokens = data?.tokens ?? [];
  const now = Date.now();
  const active = tokens.filter((t) => !t.expires_at || new Date(t.expires_at).getTime() > now);
  const expired = tokens.filter((t) => t.expires_at && new Date(t.expires_at).getTime() <= now);

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.mcp('mesh.tokens', { action: 'revoke', token_id: id }),
    onSuccess: () => {
      toast.success(t('mesh.tokenSmazan'));
      qc.invalidateQueries({ queryKey: ['mesh-tokens'] });
    },
  });

  if (tokens.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Key}>{t('mesh.existujiciTokeny', 'Existující tokeny ({{count}} aktivních)', { count: active.length })}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-2'>
        {active.map((tok) => (
          <div
            key={tok.id}
            className='flex items-center justify-between p-2.5 bg-gray-900/30 border border-gray-800 rounded-lg'
          >
            <div className='flex items-center gap-2'>
              <Key size={12} className='text-blue-400' />
              <div>
                <span className='text-xs font-medium text-white'>
                  {tok.label || tok.id.slice(0, 12)}
                </span>
                <span className='text-[10px] text-gray-500 ml-2'>
                  {tok.max_uses != null ? `${tok.used_count ?? 0}/${tok.max_uses}` : '∞'}
                  {tok.expires_at &&
                    ` · do ${
                      new Date(tok.expires_at).toLocaleDateString('cs-CZ', {
                        day: 'numeric',
                        month: 'short',
                      })
                    }`}
                </span>
              </div>
            </div>
            <div className='flex gap-1'>
              <button
                onClick={async () => {
                  navigator.clipboard.writeText(tok.token);
                  toast.success(t('mesh.zkopirovano'));
                }}
                className='p-1 text-gray-500 hover:text-white'
              >
                <Copy size={12} />
              </button>
              <button
                onClick={async () => await themedConfirm(i18n.t('mesh.smazat', 'Smazat?')) && deleteMut.mutate(tok.id)}
                className='p-1 text-gray-600 hover:text-red-400'
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
        {expired.length > 0 && (
          <details>
            <summary className='text-[10px] text-gray-600 cursor-pointer'>
              Expirované ({expired.length})
            </summary>
            <div className='space-y-1 mt-1 opacity-50'>
              {expired.map((t) => (
                <div
                  key={t.id}
                  className='flex items-center justify-between p-2 bg-gray-900/10 rounded text-xs'
                >
                  <span className='text-gray-500'>{t.label || t.id.slice(0, 12)}</span>
                  <button
                    onClick={() => deleteMut.mutate(t.id)}
                    className='p-1 text-gray-600 hover:text-red-400'
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

// ── Tenants Panel — hierarchický strom s infrastrukturou ─────────────────────

export function TenantsPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [plan, setPlan] = useState('pro');
  const [parentId, setParentId] = useState<string>('');
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);

  const { data, refetch } = useQuery({
    queryKey: ['mesh-tenants'],
    queryFn: () =>
      api.mcp<
        {
          ok: boolean;
          tenants: Array<
            {
              id: string;
              slug: string;
              name: string;
              plan: string;
              enabled: boolean;
              node_count: number;
              parent_id: string | null;
              parent_slug: string | null;
            }
          >;
        }
      >('mesh.tenants', { action: 'list' }),
  });
  const tenants = data?.tenants ?? [];

  const { data: nodesData } = useQuery({
    queryKey: ['mesh-enrolled-nodes'],
    queryFn: () =>
      api.mcp<
        {
          ok: boolean;
          nodes: Array<
            {
              node_id: string;
              hostname: string;
              tenant_id: string | null;
              status: string;
              ip_private: string;
              modules: string[];
              agent_version: string | null;
              acm_version: string | null;
              arch: string | null;
              env: string | null;
              last_seen: string | null;
              tenant_slug: string | null;
              granted_tenants?: string[];
            }
          >;
        }
      >('mesh.nodes', { action: 'list' }),
  });
  const allNodes = nodesData?.nodes ?? [];

  const createMut = useMutation({
    mutationFn: () =>
      api.mcp('mesh.tenants', { action: 'create', slug, name, plan, parent_id: parentId || null }),
    onSuccess: () => {
      toast.success(t('mesh.tenantVytvoren'));
      qc.invalidateQueries({ queryKey: ['mesh-tenants'] });
      setShowCreate(false);
      setSlug('');
      setName('');
      setParentId('');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (s: string) => api.mcp('mesh.tenants', { action: 'delete', slug: s }),
    onSuccess: () => {
      toast.success(t('mesh.smazano'));
      setSelectedTenant(null);
      qc.invalidateQueries({ queryKey: ['mesh-tenants'] });
    },
  });

  const grantMut = useMutation({
    mutationFn: ({ node_id, tenant_slug }: { node_id: string; tenant_slug: string }) =>
      api.mcp('mesh.nodes', { action: 'grant', node_id, tenant_slug }),
    onSuccess: () => {
      toast.success(t('mesh.pristupKNoduUdelen'));
      qc.invalidateQueries({ queryKey: ['mesh-enrolled-nodes'] });
      qc.invalidateQueries({ queryKey: ['mesh-tenants'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revokeMut = useMutation({
    mutationFn: ({ node_id, tenant_slug }: { node_id: string; tenant_slug: string }) =>
      api.mcp('mesh.nodes', { action: 'revoke', node_id, tenant_slug }),
    onSuccess: () => {
      toast.success(t('mesh.pristupKNoduOdebran'));
      qc.invalidateQueries({ queryKey: ['mesh-enrolled-nodes'] });
      qc.invalidateQueries({ queryKey: ['mesh-tenants'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Build tree
  type TNode = typeof tenants[0] & { children: TNode[] };
  const tree: TNode[] = (() => {
    const map = new Map(tenants.map((t) => [t.id, { ...t, children: [] as TNode[] }]));
    const roots: TNode[] = [];
    for (const t of map.values()) {
      if (t.parent_id && map.has(t.parent_id)) map.get(t.parent_id)!.children.push(t);
      else roots.push(t);
    }
    return roots;
  })();

  const sel = tenants.find((t) => t.id === selectedTenant);
  const hasTenantAccess = (
    n: { tenant_id: string | null; tenant_slug?: string | null; granted_tenants?: string[] },
    tenant?: { id: string; slug: string },
  ) =>
    !!tenant && (n.tenant_id === tenant.id || n.tenant_slug === tenant.slug ||
      (n.granted_tenants ?? []).includes(tenant.slug));
  const tenantNodes = allNodes.filter((n) => hasTenantAccess(n, sel));
  const unassigned = allNodes.filter((n) => !hasTenantAccess(n, sel));

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-sm font-semibold text-white'>{t('mesh.multiTenantInfrastruktura')}</h3>
          <p className='text-xs text-gray-500 mt-0.5'>
            {t('mesh.hierarchieTenantuPrirazeniNodu')}
          </p>
        </div>
        <div className='flex gap-2'>
          <Button variant='ghost' size='sm' onClick={() => refetch()}>
            <RefreshCw size={14} />
          </Button>
          <Button variant='primary' size='sm' onClick={() => setShowCreate(!showCreate)}>
            <Plus size={14} /> {t('mesh.novyTenant')}
          </Button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <Card className='border-blue-500/20'>
          <CardContent className='space-y-3 py-3'>
            <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
              <div>
                <label className='text-[10px] text-gray-500 block mb-1'>{t('mesh.slug')}</label>
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder='muj-tenant'
                  className='w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
                />
              </div>
              <div>
                <label className='text-[10px] text-gray-500 block mb-1'>{t('mesh.nazev')}</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('mesh.firmaSRO')}
                  className='w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
                />
              </div>
              <div>
                <label className='text-[10px] text-gray-500 block mb-1'>{t('mesh.plan')}</label>
                <Select
                  size='sm'
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                >
                  <option value='free'>Free</option>
                  <option value='pro'>Pro</option>
                  <option value='enterprise'>Enterprise</option>
                </Select>
              </div>
              <div>
                <label className='text-[10px] text-gray-500 block mb-1'>{t('mesh.nadrazenyTenant')}</label>
                <Select
                  size='sm'
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                >
                  <option value=''>{t('mesh.rootMaxcloud')}</option>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
                </Select>
              </div>
            </div>
            <div className='flex justify-end gap-2'>
              <Button variant='secondary' size='sm' onClick={() => setShowCreate(false)}>
                {t('mesh.zrusit')}
              </Button>
              <Button
                variant='primary'
                size='sm'
                onClick={() => createMut.mutate()}
                disabled={!slug || !name}
                loading={createMut.isPending}
              >
                {t('mesh.vytvorit')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {tenants.length === 0
        ? <EmptyState title={t('mesh.zadniTenanti')} description={t('mesh.vytvortePrvnihoTenanta')} />
        : (
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
            {/* Left: Tree */}
            <Card>
              <CardHeader>
                <CardTitle className='text-sm'>{t('mesh.stromTenantu')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-0.5'>
                  {/* Root organizace — MaxCloud */}
                  <div className='p-2.5 rounded-lg text-xs flex items-center gap-2 bg-amber-500/5 border border-amber-500/20 mb-1'>
                    <span className='w-2.5 h-2.5 rounded-full bg-amber-400' />
                    <span className='font-semibold text-white'>MaxCloud</span>
                    <span className='text-[10px] text-gray-500 ml-auto'>
                      {tenants.length} {t('mesh.tenantu', 'tenantů ·')} {allNodes.length} {t('mesh.nodu', 'nodů')}
                    </span>
                  </div>
                  {tree.map((node) => (
                    <TenantTreeNode
                      key={node.id}
                      node={node}
                      depth={1}
                      selected={selectedTenant}
                      onSelect={setSelectedTenant}
                      allNodes={allNodes}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Right: Detail */}
            <div className='lg:col-span-2 space-y-4'>
              {!sel
                ? (
                  <Card>
                    <CardContent className='py-6'>
                      <EmptyState
                        title={t('mesh.vyberteTenanta')}
                        description={t('mesh.klikneteNaTenantaVe')}
                      />
                    </CardContent>
                  </Card>
                )
                : (
                  <>
                    {/* Header */}
                    <Card>
                      <CardContent className='py-3'>
                        <div className='flex items-center justify-between'>
                          <div className='flex items-center gap-3'>
                            <Users size={18} className='text-blue-400' />
                            <div>
                              <span className='text-sm font-bold text-white'>{sel.name}</span>
                              <span className='ml-2 text-[10px] text-gray-500 font-mono'>
                                {sel.slug}
                              </span>
                              <Badge className='ml-2 text-[9px]'>{sel.plan}</Badge>
                              {sel.parent_slug && (
                                <span className='ml-2 text-[10px] text-cyan-400'>
                                  pod {sel.parent_slug}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            variant='danger'
                            size='sm'
                            onClick={async () => await themedConfirm(`Smazat ${sel.name}?`) && deleteMut.mutate(sel.slug)}
                          >
                            <Trash2 size={12} /> {t('common.delete', 'Smazat')}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Nody */}
                    <Card>
                      <CardHeader>
                        <CardTitle className='text-sm'>
                          <Server size={14} className='inline mr-1' />{' '}
                          {t('mesh.nodyPristupem', 'Nody s přístupem ({{count}})', { count: tenantNodes.length })}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-2'>
                        {tenantNodes.length === 0 && (
                          <p className='text-xs text-gray-600 italic'>{t('mesh.zadneNodyPrirazene')}</p>
                        )}
                        {tenantNodes.map((n) => (
                          <NodeCard
                            key={n.node_id}
                            node={n}
                            size='md'
                            actions={
                              <button
                                onClick={() =>
                                  revokeMut.mutate({ node_id: n.node_id, tenant_slug: sel.slug })}
                                className='text-[10px] px-1.5 py-0.5 rounded text-gray-600 hover:text-red-400 hover:bg-red-900/20'
                              >
                                {t('mesh.odebratPristup')}
                              </button>
                            }
                          />
                        ))}

                        {/* Nepřiřazené */}
                        {unassigned.length > 0 && (
                          <div className='mt-3 pt-3 border-t border-gray-800'>
                            <span className='text-[10px] text-amber-400/70 font-medium'>
                              {t('mesh.dostupneNodyUdelitPristup', 'Dostupné nody (udělit přístup) ({{count}})', { count: unassigned.length })}
                            </span>
                            <div className='space-y-1 mt-1'>
                              {unassigned.map((n) => (
                                <NodeCard
                                  key={n.node_id}
                                  node={n}
                                  size='sm'
                                  actions={
                                    <button
                                      onClick={() =>
                                        grantMut.mutate({
                                          node_id: n.node_id,
                                          tenant_slug: sel.slug,
                                        })}
                                      className='text-[10px] px-2 py-0.5 rounded bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30'
                                    >
                                      {t('mesh.udelitPristup')}
                                    </button>
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Moduly */}
                    <TenantModules slug={sel.slug} />
                  </>
                )}
            </div>
          </div>
        )}
    </div>
  );
}

// Rekurzivní tree node pro infrastrukturní pohled
function TenantTreeNode({ node, depth, selected, onSelect, allNodes }: {
  node: {
    id: string;
    slug: string;
    name: string;
    plan: string;
    enabled: boolean;
    node_count: number;
    children: Array<
      {
        id: string;
        slug: string;
        name: string;
        plan: string;
        enabled: boolean;
        node_count: number;
        children: unknown[];
      }
    >;
  };
  depth: number;
  selected: string | null;
  onSelect: (id: string) => void;
  allNodes: Array<
    { tenant_id: string | null; tenant_slug?: string | null; granted_tenants?: string[] }
  >;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const isSelected = selected === node.id;
  const nodeCount = allNodes.filter((n) =>
    n.tenant_id === node.id || n.tenant_slug === node.slug ||
    (n.granted_tenants ?? []).includes(node.slug)
  ).length;

  return (
    <div>
      <button
        onClick={() => onSelect(node.id)}
        className={`w-full text-left p-2 rounded-lg text-xs flex items-center gap-2 transition-colors ${
          isSelected
            ? 'bg-blue-950/40 border border-blue-500/30'
            : 'hover:bg-gray-800/40 border border-transparent'
        }`}
        style={{ paddingLeft: depth * 20 + 8 }}
      >
        {hasChildren
          ? (
            <span
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              className='p-0.5 hover:bg-gray-700 rounded cursor-pointer'
            >
              <ChevronRight
                size={12}
                className={`transition-transform ${expanded ? 'rotate-90' : ''}`}
              />
            </span>
          )
          : <span className='w-4' />}
        <Users size={13} className={node.enabled ? 'text-blue-400' : 'text-gray-600'} />
        <span className='font-medium text-white'>{node.name}</span>
        <Badge className='text-[9px]'>{node.plan}</Badge>
        <span className='text-[10px] text-gray-500 ml-auto'>
          {nodeCount > 0
            ? t('mesh.nodeCount', '{{count}} nodů', { count: nodeCount })
            : t('mesh.noNodes', 'bez nodů')}
        </span>
      </button>
      {expanded && hasChildren &&
        node.children.map((child: typeof node) => (
          <TenantTreeNode
            key={child.id}
            node={child as typeof node}
            depth={depth + 1}
            selected={selected}
            onSelect={onSelect}
            allNodes={allNodes}
          />
        ))}
    </div>
  );
}

function TenantModules({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['mesh-tenant-modules', slug],
    queryFn: () =>
      api.mcp<{ ok: boolean; modules: string[] }>('mesh.tenants', {
        action: 'modules',
        slug,
      }),
  });
  const modules = data?.modules ?? [];
  return (
    <div>
      <h4 className='text-xs font-medium text-gray-400 mb-1'>{t('mesh.povoleneModuly')}</h4>
      {modules.length === 0
        ? (
          <p className='text-[10px] text-gray-600 italic'>
            {t('mesh.vsechnyModulyPovolenyZadne')}
          </p>
        )
        : (
          <div className='flex flex-wrap gap-1'>
            {modules.map((m) => <Badge key={m} variant='blue'>{m}</Badge>)}
          </div>
        )}
    </div>
  );
}

// ── Discovered Panel — pending nodes awaiting MCM approval ───────────────────

interface DiscoveredNode {
  id: string;
  node_id: string;
  hostname: string;
  advertised_ip: string;
  api_port: number;
  cert_fingerprint: string | null;
  acm_version: string | null;
  arch: string | null;
  bootstrap_method: 'pxe' | 'clean_debian' | 'manual';
  default_credentials_used: boolean;
  first_seen: string;
  last_seen: string;
  state: string;
  /** public | cert_review | identity_verified | paired */
  stage?: string;
  stable_id?: string;
  cert_subject?: string | null;
  next?: string | null;
}

function DiscoveredTabLabel() {
  const { data } = useQuery<{ ok: boolean; total: number }>({
    queryKey: ['mesh-discovered-count'],
    queryFn: () => api.mcp('mesh.discovery_list', { state: 'pending' }),
    refetchInterval: 15_000,
  });
  const count = data?.total ?? 0;
  return (
    <span>
      Pending {count > 0 && <Badge variant='warning'>{count}</Badge>}
    </span>
  );
}

export function DiscoveredPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [approveTarget, setApproveTarget] = useState<DiscoveredNode | null>(null);
  const [rejectTarget, setRejectTarget] = useState<DiscoveredNode | null>(null);
  const [pairTenant, setPairTenant] = useState('');

  const tenantsQ = useQuery({
    queryKey: ['mesh-tenants'],
    queryFn: () =>
      api.mcp<{ ok: boolean; tenants: Array<{ slug: string; name: string }> }>(
        'mesh.tenants',
        { action: 'list' },
      ),
  });
  const pairTenants = tenantsQ.data?.tenants ?? [];
  const { data: me } = useQuery({
    queryKey: IDENTITY_ME_QUERY_KEY,
    queryFn: () => api.mcp<IdentityMe>('identity.me'),
    staleTime: 60_000,
  });
  const myId = identityEntityId(me);

  const { data, isLoading } = useQuery<{ ok: boolean; total: number; nodes: DiscoveredNode[] }>({
    queryKey: ['mesh-discovered'],
    queryFn: () => api.mcp('mesh.discovery_list', { state: 'pending' }),
    refetchInterval: 10_000,
  });

  const nodes = data?.nodes ?? [];

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={Wifi}>
            {t('mesh.objeveneNodyCekajiciNa')} <DiscoveredTabLabel />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-xs text-zinc-400 mb-4'>
            {t('mesh.spolecnyDiscoveryLanIp', 'Společný discovery (LAN IP i veřejná URL) má stupně:')}{' '}
            <strong className='text-zinc-300'>public</strong> (GET /mcp — identita bez lokací) →{' '}
            <strong className='text-zinc-300'>cert_review</strong> (schválit cert/CA) →{' '}
            <strong className='text-zinc-300'>identity_verified</strong> (mTLS well-known) →{' '}
            <strong className='text-zinc-300'>paired</strong> (
            <code>mesh.trust_pair</code> remote / Approve = cluster full-mesh).
            {t('mesh.toolsRightsByEntity', 'Práva toolů dál řídí volající entita.')}
          </p>

          <div className='flex gap-2 mb-3'>
            <Select
              value={pairTenant}
              onChange={(e) => setPairTenant(e.target.value)}
              className='px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white'
            >
              <option value=''>{t('mesh.pickTenant', 'Tenant…')}</option>
              {pairTenants.map((tn) => (
                <option key={tn.slug} value={tn.slug}>{tn.name} ({tn.slug})</option>
              ))}
            </Select>
          </div>

          {isLoading
            ? <div className='text-zinc-500 text-sm'>{t('mesh.nacitani')}</div>
            : nodes.length === 0
            ? (
              <EmptyState
                icon={Wifi}
                title={t('mesh.zadneNodyNecekajiNa')}
                description={t('mesh.spawnNovyNodPres')}
              />
            )
            : (
              <div className='overflow-x-auto'>
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='border-b border-zinc-800 text-xs text-zinc-500 text-left'>
                      <th className='py-2 pr-3'>{t('mesh.hostnameNodeId')}</th>
                      <th className='py-2 pr-3'>IP</th>
                      <th className='py-2 pr-3'>Stage</th>
                      <th className='py-2 pr-3'>Method</th>
                      <th className='py-2 pr-3'>Fingerprint</th>
                      <th className='py-2 pr-3'>{t('mesh.lastSeen')}</th>
                      <th className='py-2 text-right'>Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodes.map((n) => (
                      <tr key={n.id} className='border-b border-zinc-900 hover:bg-zinc-900/50'>
                        <td className='py-2 pr-3'>
                          <div className='font-medium text-zinc-200'>{n.hostname || n.node_id}</div>
                          <div className='text-xs text-zinc-500 font-mono'>
                            {n.stable_id || n.node_id}
                          </div>
                          {n.cert_subject && (
                            <div className='text-[10px] text-zinc-600 truncate max-w-[200px]' title={n.cert_subject}>
                              {n.cert_subject}
                            </div>
                          )}
                        </td>
                        <td className='py-2 pr-3 font-mono text-xs'>
                          {n.advertised_ip}:{n.api_port}
                        </td>
                        <td className='py-2 pr-3'>
                          <Badge
                            variant={n.stage === 'identity_verified'
                              ? 'green'
                              : n.stage === 'cert_review'
                              ? 'yellow'
                              : n.stage === 'paired'
                              ? 'blue'
                              : 'gray'}
                          >
                            {n.stage ?? 'pending'}
                          </Badge>
                        </td>
                        <td className='py-2 pr-3'>
                          <Badge
                            variant={n.bootstrap_method === 'pxe'
                              ? 'blue'
                              : n.bootstrap_method === 'clean_debian'
                              ? 'green'
                              : 'gray'}
                          >
                            {n.bootstrap_method}
                          </Badge>
                          {n.default_credentials_used && (
                            <span title={t('mesh.defaultCredentials')} className='ml-1 text-orange-400'>
                              <AlertTriangle size={12} className='inline' />
                            </span>
                          )}
                        </td>
                        <td className='py-2 pr-3'>
                          <code className='text-xs text-zinc-400 truncate max-w-[160px] inline-block'>
                            {n.cert_fingerprint?.slice(0, 24) ?? '—'}…
                          </code>
                        </td>
                        <td className='py-2 pr-3 text-xs text-zinc-500'>
                          {new Date(n.last_seen).toLocaleString('cs-CZ')}
                        </td>
                        <td className='py-2 text-right space-x-1'>
                          <Button
                            variant='primary'
                            size='sm'
                            title={t('mesh.connectRemote', 'Připojit remote')}
                            onClick={() => {
                              const fp = n.cert_fingerprint;
                              if (!fp) {
                                toast.error(
                                  t(
                                    'mesh.missingCertFingerprint',
                                    'Chybí cert fingerprint — nejdřív spusť discovery s kontrolou certifikátu',
                                  ),
                                );
                                return;
                              }
                              if (!pairTenant) {
                                toast.error(t('mesh.pickTenantFirst', 'Nejdřív vyber tenant'));
                                return;
                              }
                              void api.mcp('mesh.remote_add', {
                                discovery_id: n.id,
                                confirmed_fingerprint: fp,
                                tenant_slug: pairTenant,
                                label: n.hostname || n.stable_id || n.node_id,
                                enroll: true,
                                bind_users: myId
                                  ? [{
                                    entity_id: myId,
                                    modules: [
                                      { module: 'mesh', role: 'admin' },
                                      { module: 'acm-self', role: 'operator' },
                                    ],
                                  }]
                                  : undefined,
                              }).then((res: { ok?: boolean; error?: string; stable_id?: string }) => {
                                if (res?.ok) {
                                  toast.success(t('mesh.remoteAdded', 'Remote {{id}} připojen', {
                                    id: res.stable_id ?? n.hostname,
                                  }));
                                  void qc.invalidateQueries({ queryKey: ['mesh-discovered'] });
                                  void qc.invalidateQueries({ queryKey: ['mesh-trust-overview'] });
                                  void qc.invalidateQueries({ queryKey: ['mesh-topology'] });
                                } else toast.error(res?.error ?? t('mesh.remoteAddFailed', 'Přidání remote selhalo'));
                              }).catch((e: Error) => toast.error(e.message));
                            }}
                          >
                            {t('mesh.connectRemote', 'Připojit remote')}
                          </Button>
                          <Button
                            variant='success'
                            size='sm'
                            title='Cluster full-mesh approve'
                            onClick={() => setApproveTarget(n)}
                          >
                            Cluster
                          </Button>
                          <Button
                            variant='danger'
                            size='sm'
                            onClick={() => setRejectTarget(n)}
                          >
                            Reject
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </CardContent>
      </Card>

      {approveTarget && (
        <ApproveDiscoveredModal
          node={approveTarget}
          onClose={() => setApproveTarget(null)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['mesh-discovered'] });
            qc.invalidateQueries({ queryKey: ['mesh-discovered-count'] });
            qc.invalidateQueries({ queryKey: ['mesh-peers'] });
            setApproveTarget(null);
          }}
        />
      )}
      {rejectTarget && (
        <RejectDiscoveredModal
          node={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['mesh-discovered'] });
            qc.invalidateQueries({ queryKey: ['mesh-discovered-count'] });
            setRejectTarget(null);
          }}
        />
      )}
    </div>
  );
}

function ApproveDiscoveredModal({
  node,
  onClose,
  onSuccess,
}: {
  node: DiscoveredNode;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(node.hostname || node.node_id);
  const [confirmFingerprint, setConfirmFingerprint] = useState('');
  const [skipBackup, setSkipBackup] = useState(false);
  const [republish, setRepublish] = useState(true);

  const mutation = useMutation({
    mutationFn: () =>
      api.mcp('mesh.node_approve', {
        discovery_id: node.id,
        confirmed_fingerprint: confirmFingerprint,
        label,
        skip_backup: skipBackup,
        republish,
      }),
    onSuccess: (response: { ok?: boolean; error?: string; steps?: unknown }) => {
      if (response.ok) {
        toast.success(t('mesh.nodeJoinedMesh', 'Node {{label}} přidán do mesh', { label }));
        onSuccess();
      } else {
        toast.error(response.error ?? 'Approve failed');
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const fingerprintMatches = node.cert_fingerprint
    ? confirmFingerprint === node.cert_fingerprint
    : confirmFingerprint === 'I CONFIRM';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Approve nod: ${node.hostname || node.node_id}`}
      size='lg'
    >
      <div className='space-y-4'>
        <div className='bg-zinc-900 border border-zinc-800 rounded p-3 text-sm space-y-1'>
          <div>
            <span className='text-zinc-500'>{t('mesh.nodeId')}</span> <code>{node.node_id}</code>
          </div>
          <div>
            <span className='text-zinc-500'>{t('mesh.ip')}</span>{' '}
            <code>{node.advertised_ip}:{node.api_port}</code>
          </div>
          <div>
            <span className='text-zinc-500'>{t('mesh.bootstrap')}</span> {node.bootstrap_method}
          </div>
          {node.default_credentials_used && (
            <div className='text-orange-400 text-xs flex items-center gap-1'>
              <AlertTriangle size={12} /> {t('mesh.pouzitoDefaultHesloAdmin')}
            </div>
          )}
          <div className='pt-2 border-t border-zinc-800'>
            <div className='text-zinc-500 text-xs'>{t('mesh.certFingerprintZkopirujA')}</div>
            <code className='block text-xs text-zinc-300 break-all bg-zinc-950 p-2 rounded mt-1'>
              {node.cert_fingerprint ?? '—'}
            </code>
          </div>
        </div>

        <div>
          <label className='block text-xs text-zinc-400 mb-1'>{t('mesh.friendlyLabelCcpName')}</label>
          <input
            type='text'
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className='w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm'
          />
        </div>

        <div>
          <label className='block text-xs text-zinc-400 mb-1'>
            {t('mesh.confirmFingerprintVlozCely')}
          </label>
          <input
            type='text'
            value={confirmFingerprint}
            onChange={(e) => setConfirmFingerprint(e.target.value)}
            placeholder={node.cert_fingerprint ?? 'I CONFIRM'}
            className='w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm font-mono'
          />
          {confirmFingerprint && !fingerprintMatches && (
            <div className='text-xs text-red-400 mt-1'>{t('mesh.fingerprintNesouhlasi')}</div>
          )}
        </div>

        <div className='space-y-1 text-sm'>
          <label className='flex items-center gap-2'>
            <input
              type='checkbox'
              checked={!skipBackup}
              onChange={(e) => setSkipBackup(!e.target.checked)}
            />
            <span>{t('mesh.backupStavajicihoStavuDoporuce')}</span>
          </label>
          <label className='flex items-center gap-2'>
            <input
              type='checkbox'
              checked={republish}
              onChange={(e) => setRepublish(e.target.checked)}
            />
            <span>{t('mesh.rePublishLokalniEntries')}</span>
          </label>
        </div>

        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='secondary' onClick={onClose}>{t('mesh.zrusit')}</Button>
          <Button
            variant='success'
            disabled={!fingerprintMatches || !label || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending
              ? t('mesh.approvePending', 'Probíhá approve...')
              : t('mesh.approveJoin', 'Approve a join do mesh')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RejectDiscoveredModal({
  node,
  onClose,
  onSuccess,
}: {
  node: DiscoveredNode;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);

  const mutation = useMutation({
    mutationFn: () =>
      api.mcp('mesh.node_reject', {
        discovery_id: node.id,
        reason,
        notify,
      }),
    onSuccess: (response: { ok?: boolean; error?: string }) => {
      if (response.ok) {
        toast.success(`Node ${node.node_id} rejected`);
        onSuccess();
      } else {
        toast.error(response.error ?? 'Reject failed');
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Reject nod: ${node.hostname || node.node_id}`}
      size='md'
    >
      <div className='space-y-4'>
        <div className='text-sm text-zinc-400'>
          {t(
            'mesh.rejectHint',
            'Označí discovery záznam jako rejected. Volitelně publish MQTT notify na cílový nod (nod si přečte a samolikvidační handler smaže temp creds).',
          )}
        </div>
        <div>
          <label className='block text-xs text-zinc-400 mb-1'>{t('mesh.duvodPovinny')}</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className='w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm'
            placeholder={t('mesh.naprNeznamyNodNespravny')}
          />
        </div>
        <label className='flex items-center gap-2 text-sm'>
          <input
            type='checkbox'
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
          />
          <span>{t('mesh.notifyCilovyNodPublish')}</span>
        </label>
        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='secondary' onClick={onClose}>{t('mesh.zavrit')}</Button>
          <Button
            variant='danger'
            disabled={!reason.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? t('mesh.rejectPending', 'Probíhá reject...') : 'Reject'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Node Role Status — info badge (master = vlastní CA, peer = cizí CA) ──────

export function NodeRoleStatus() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<{ role?: string; node_id?: string; peer_count?: number }>({
    queryKey: ['mesh-local-status-role'],
    // Background poll — bez toastu.
    queryFn: () => api.mcp('mesh.local_status', {}, { silent: true }),
    refetchInterval: 60_000,
  });
  if (isLoading) {
    return <div className='text-xs text-gray-500 py-2'>{t('mesh.loadingRole', 'Načítám roli…')}</div>;
  }
  if (!data?.role) {
    return <div className='text-xs text-gray-600 py-2'>{t('mesh.roleUnavailable', 'Role nedostupná')}</div>;
  }
  const isMaster = data.role === 'master';
  return (
    <div className='flex flex-col gap-2 py-1'>
      <Badge variant={isMaster ? 'green' : 'blue'} className='w-fit'>
        {isMaster ? t('mesh.roleMcm', 'MCM (vlastní CA)') : t('mesh.rolePeer', 'Peer (cizí CA)')}
      </Badge>
      <div className='text-xs text-gray-400'>
        node <code className='text-gray-200'>{data.node_id}</code>
        {isMaster && (
          <span className='text-gray-500'>
            {' '}· {t('mesh.peerCount', '{{count}} peerů v mesh', { count: data.peer_count ?? 0 })}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Bootstrap Panel — spawn URL + creds + rotate ─────────────────────────────

interface BootstrapInfo {
  ok: boolean;
  mcm_host: string;
  spawn_url: string;
  pxe_preseed_url: string;
  curl_command: string;
  bootstrap_user: string;
  bootstrap_pass: string;
  bootstrap_pass_set: boolean;
}

export function BootstrapPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [revealPass, setRevealPass] = useState(false);

  const { data, isLoading } = useQuery<BootstrapInfo>({
    queryKey: ['mesh-bootstrap-info'],
    queryFn: () => api.mcp<BootstrapInfo>('mesh.bootstrap_info', {}),
  });

  const rotateMutation = useMutation({
    mutationFn: (args: { rotate_pass: boolean }) =>
      api.mcp<{ ok?: boolean; new_bootstrap_pass?: string }>('mesh.bootstrap_rotate', args),
    onSuccess: (resp: { ok?: boolean; new_bootstrap_pass?: string }) => {
      if (resp.ok) {
        toast.success(t('mesh.rotatedRestartAcmApi'));
        qc.invalidateQueries({ queryKey: ['mesh-bootstrap-info'] });
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('mesh.copiedLabel', '{{label}} zkopírováno', { label }));
  };

  if (isLoading || !data) {
    return <div className='text-zinc-500 text-sm'>{t('mesh.nacitani')}</div>;
  }

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={Key}>{t('mesh.spawnNovehoAcmNodu')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-xs text-zinc-400 mb-3'>
            {t('mesh.spustitNaCistemDebianu')}
          </p>
          <div className='bg-zinc-950 border border-zinc-800 rounded p-3 mb-3 flex items-center gap-2'>
            <code className='flex-1 text-xs text-emerald-300 font-mono break-all'>
              {data.curl_command}
            </code>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => copyToClipboard(data.curl_command, t('mesh.curlCommand', 'Curl příkaz'))}
            >
              <Copy size={14} />
            </Button>
          </div>

          <div className='grid grid-cols-2 gap-3 text-xs'>
            <div>
              <div className='text-zinc-500 mb-1'>{t('mesh.spawnSkriptUrl')}</div>
              <div className='bg-zinc-900 border border-zinc-800 rounded px-2 py-1 flex items-center gap-2'>
                <code className='flex-1 text-zinc-300 break-all'>{data.spawn_url}</code>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => copyToClipboard(data.spawn_url, 'URL')}
                >
                  <Copy size={12} />
                </Button>
              </div>
            </div>
            <div>
              <div className='text-zinc-500 mb-1'>{t('mesh.pxePreseedUrl')}</div>
              <div className='bg-zinc-900 border border-zinc-800 rounded px-2 py-1 flex items-center gap-2'>
                <code className='flex-1 text-zinc-300 break-all'>{data.pxe_preseed_url}</code>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => copyToClipboard(data.pxe_preseed_url, 'URL')}
                >
                  <Copy size={12} />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle icon={Lock}>{t('mesh.bootstrapCredentials')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <div>
            <div className='flex items-center justify-between mb-1'>
              <label className='text-xs text-zinc-400'>
                ACM_DEFAULT_BOOTSTRAP_PASS
                {data.bootstrap_pass_set
                  ? <Badge variant='green' className='ml-2'>set</Badge>
                  : <Badge variant='red' className='ml-2'>missing</Badge>}
              </label>
              <Button
                variant='warning'
                size='sm'
                onClick={() => rotateMutation.mutate({ rotate_pass: true })}
                disabled={rotateMutation.isPending}
              >
                {t('mesh.rotatePassword')}
              </Button>
            </div>
            <div className='bg-zinc-950 border border-zinc-800 rounded px-2 py-1 flex items-center gap-2'>
              <code className='flex-1 text-xs font-mono text-zinc-300 break-all'>
                {revealPass
                  ? data.bootstrap_pass || '(empty)'
                  : data.bootstrap_pass
                  ? '••••••••'
                  : '(empty)'}
              </code>
              <Button variant='ghost' size='sm' onClick={() => setRevealPass(!revealPass)}>
                {revealPass ? 'Hide' : 'Reveal'}
              </Button>
              {data.bootstrap_pass && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => copyToClipboard(data.bootstrap_pass, 'Password')}
                >
                  <Copy size={12} />
                </Button>
              )}
            </div>
            <div className='text-[10px] text-zinc-500 mt-1'>
              {t(
                'mesh.bootstrapPassHint',
                'Default heslo pro user "{{user}}" na nově nainstalovaných nodech. Po approvalu admin musí rotovat na cílovém nodu.',
                { user: data.bootstrap_user },
              )}
            </div>
          </div>

          <div className='bg-amber-950/30 border border-amber-800/50 rounded p-2 text-[11px] text-amber-200'>
            <AlertTriangle size={12} className='inline mr-1' />
            {t('mesh.poRotateMusisRestartovat')} <code>sudo systemctl restart acm-api</code>{' '}
            {t('mesh.envReloadNoveSpawned', '(env reload). Nově spawned nody pak dostanou nové creds.')}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle icon={Wifi}>{t('mesh.mqttDiscoveryVolitelne')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-xs text-zinc-400 mb-2'>
            {t(
              'mesh.mqttDiscoveryHint',
              'Alternativa k HTTP webhook — nový nod publikuje announcement na EMQX. Vyžaduje pre-vytvořený MQTT user',
            )}{' '}
            <code>acm_discovery</code>
            {t('mesh.mqttDiscoveryHintAcl', 's ACL omezeným na publish')}{' '}
            <code>acm/discovery/v1/announce/+</code>.
          </p>
          <div className='bg-zinc-950 border border-zinc-800 rounded p-2 text-xs font-mono text-zinc-400'>
            {`mosquitto_pub -h ${data.mcm_host} -p 1883 \\
  -u acm_discovery -P <pass> \\
  -t 'acm/discovery/v1/announce/{node_id}' \\
  -m '{"node_id":"...","advertised_ip":"...","bootstrap_method":"manual"}'`}
          </div>
          <div className='text-[10px] text-zinc-500 mt-2'>
            {t(
              'mesh.mqttAclHint',
              'ACL je omezený přes EMQX built_in_database. mcp-proxy/proxy.py subscribuje na tento topic a forwarduje na webhook.',
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
